'use client';

import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  type ReactNode
} from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslations } from 'next-intl';
import {
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  Copy,
  Clock,
  History,
  Layers,
  Check,
  Columns2,
  Loader2,
  Maximize2,
  Pencil,
  Plus,
  Redo2,
  RotateCcw,
  ScanText,
  Undo2,
  ShieldCheck,
  TriangleAlert,
  Trophy,
  X,
  XCircle
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { PanelResizeHandle } from '@/components/panel-resize-handle';
import { toast } from 'sonner';
import { useRouter } from '@/i18n/routing';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePanelWidth } from '@/hooks/use-panel-width';
import { useUrlHash } from '@/hooks/use-url-hash';
import { API_ENDPOINTS } from '@/lib/api/config';
import {
  useOcrDefaultHighlightColor,
  useOcrHighlightsEnabled
} from '@/features/configurations';
import { TemplateFieldsInfo } from '@/features/ocr-templates';
import {
  PermissionGate,
  hasAnyPermission,
  isSuperAdmin
} from '@/features/auth';
import { useAuth } from '@/contexts/auth-context';
import { useQuery } from '@tanstack/react-query';
import { ocrQueries } from '../services/ocr.queries';
import { useContainerResultsLive } from '../hooks/use-container-results-live';
import { useResultsPanel } from '../hooks/use-results-panel';
import { useResultsSidebar } from '../hooks/use-results-sidebar';
import { useFieldHighlightColors } from '../hooks/use-field-highlight-colors';
import { resolveFieldHighlightColor } from '../utils/highlight-color';
import { bucketFieldsByPage } from '../utils/fields-by-page';
import { partitionFields } from '../utils/partition-fields';
import { withFirstGroupPerResult } from '../utils/first-group-per-result';
import { resolveEmptyResultAnchor } from '../utils/empty-result-anchor';
import { legacyViewerPage } from '../utils/legacy-viewer-page';
import { nextParkedViewerPage, resolveViewerPage } from '../utils/viewer-page';
import {
  parseResultsDeepLink,
  type ResultsDeepLink
} from '../utils/results-deep-link';
import { type DisplayOcrStatus } from '../utils/run-status';
import {
  coversNoPage,
  emptyResultSectionPages,
  isPageInFlight,
  pageDisplayStatus,
  rerunningPagesInFlight,
  runProgressPercent,
  sectionPageNumbers
} from '../utils/page-run-state';
import { resolveOcrContainerLabel } from '../utils/container-label';
import { canReopenVerifiedFields } from '../utils/field-edit-access';
import { pageQaBucket } from '../utils/page-qa-bucket';
import {
  resolvePanelCapabilities,
  type OcrPanelSurface
} from '../utils/panel-capabilities';
import { resultLocalPageNumber } from '../utils/highlight-page-number';
import { narrowResultFields } from '../utils/narrow-fields';
import {
  addHighlight,
  discardAddition,
  highlightDraftSize,
  moveHighlight,
  recolorField,
  removeHighlight,
  removedChunkIds
} from '../utils/highlight-draft';
import { useHighlightDraft } from '../hooks/use-highlight-draft';
import {
  validateManualFieldDraft,
  type ManualFieldDraft
} from '../utils/manual-field-draft';
import {
  useAcceptOcrFieldMutation,
  useAcceptAllOcrFieldsMutation,
  useAcceptAllOcrFieldsOnPageMutation,
  useRerunOcrPageMutation,
  useStartOcrMutation,
  useSubmitContainerToQaMutation,
  useCreateFieldHighlightMutation,
  useCreateOcrFieldMutation,
  useDeleteFieldHighlightMutation,
  useUpdateChunkBboxMutation,
  useUpdateOcrFieldMutation,
  useRenameOcrFieldMutation
} from '../services/ocr.mutations';
import { formatDuration } from '@/lib/format-duration';
import { AddFieldDialog } from './add-field-dialog';
import { OcrTemplatePoolDialog } from './ocr-template-pool-dialog';
import { ContainerIdPrefix } from './container-id-prefix';
import { DocumentPagesViewer } from './document-pages-viewer';
import { ExtractionTimeline } from './extraction-timeline';
import { OcrErrorCard } from './ocr-error-card';
import { ExtractedFieldCard } from './extracted-field-card';
import {
  ExtractedDataSearch,
  type ExtractedFieldHit
} from './extracted-data-search';
import { SuggestionHistoryPanel } from './suggestion-history-panel';
import type {
  ExtractedDataItem,
  ExtractedFieldSource,
  OcrPage,
  OcrQaStage,
  OcrResult,
  OcrSuggestionRunDetail,
  UpdateChunkBboxRequest
} from '../types';
import type { StepState } from './extraction-timeline';
import type { ViewerPage } from './document-pages-viewer';

// ── Helpers ────────────────────────────────────────────────────────────────

// `key` alone is not unique: the model provider can emit the same `custom_7` once per page,
// so we use the field's first chunk reference as a tie-breaker. `ref` is `""`
// for refless fields (e.g. empty values) — those can't be highlighted in the
// viewer anyway because there's no chunk to target.
interface FieldId {
  resultId: number;
  key: string;
  ref: string;
}

function fieldRef(field: ExtractedDataItem): string {
  return field.references[0] ?? '';
}

function sameField(
  a: FieldId | null,
  resultId: number,
  field: ExtractedDataItem
): boolean {
  return (
    a != null &&
    a.resultId === resultId &&
    a.key === field.key &&
    a.ref === fieldRef(field)
  );
}

/**
 * `pageIndexInResult` of the unattributed section. Deliberately outside the
 * 1-based page space: `pageQaBucket` looks it up in `page_notes` (never set for
 * `0`, so the section reads as "not reviewed"), the container page's footer
 * gates its QA comments on `=== 1`, and the pool winner map is keyed by real
 * page numbers — all three no-op on `0` instead of borrowing page 1's state.
 */
const UNATTRIBUTED_PAGE_INDEX = 0;

/**
 * How many template names the header spells out before collapsing the rest
 * into a `+N`. The pool is usually one template and rarely more than three;
 * the cap exists so a wide pool cannot push the header's actions off the row.
 */
const HEADER_TEMPLATE_LIMIT = 3;

/**
 * One accordion section in the right panel. A single OcrResult can span
 * multiple pages, so we explode it into one PageGroup per page that owns
 * fields. Fields are bucketed by `bucketFieldsByPage` — the page the first
 * resolvable chunk reference points at (via `chunks[].page_number`), or the
 * page a repeating template stamped on the item.
 */
interface PageGroup {
  result: OcrResult;
  page: OcrPage;
  /**
   * 1-based, or `UNATTRIBUTED_PAGE_INDEX` on the unattributed section — which
   * addresses no page, so it must never be passed to a per-page endpoint
   * (re-run, QA page notes) as if it were one.
   */
  pageIndexInResult: number;
  viewerPageIndex: number; // index into the flat viewerPages array
  fields: ExtractedDataItem[];
  /**
   * This section holds the result's fields that resolved to no page at all,
   * not a page's fields. They stay verifiable here (accept/edit key off the
   * field's own `page_number`, not the section) but are excluded from every
   * page-scoped affordance.
   */
  isUnattributed: boolean;
  /** True on the first rendered group of this result — the anchor for
   * per-result affordances like the QA verdict buttons. Set by
   * `withFirstGroupPerResult` after sorting and filtering. */
  isFirstGroupForResult: boolean;
}

/** A `PageGroup` tagged with its position in the flat `pageGroups` array —
 * `collapsedGroups` / `handleSetGroupOpen` / `pageSectionRefs` are all keyed
 * by this original index, so clustering must carry it through unchanged. */
interface PageGroupWithIndex extends PageGroup {
  groupIndex: number;
}

/**
 * One or more `pageGroups` entries that resolve to the same physical page —
 * i.e. pool siblings (different candidate templates run against the same
 * page). Rendered as a single section when there's only one entry, or a
 * "template pool" cluster with a shared header when there are 2+.
 */
interface PageCluster {
  key: string;
  entries: PageGroupWithIndex[];
}

function getStepStates(
  status: DisplayOcrStatus,
  hasFields: boolean
): [StepState, StepState, StepState] {
  if (status === 'completed') return ['done', 'done', 'done'];
  if (hasFields) return ['done', 'active', 'waiting'];
  if (status === 'processing') return ['active', 'waiting', 'waiting'];
  return ['waiting', 'waiting', 'waiting'];
}

// No `pending` arm: `displayOcrStatus` folds it into `processing`, matching
// the listing's collapsed `ocr_status`.
const STATUS_BADGE: Record<
  DisplayOcrStatus,
  { className: string; labelKey: string }
> = {
  processing: {
    className:
      'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400',
    labelKey: 'statusProcessing'
  },
  paused: {
    className:
      'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400',
    labelKey: 'statusPaused'
  },
  completed: {
    className:
      'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    labelKey: 'statusCompleted'
  },
  completed_with_errors: {
    className:
      'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400',
    labelKey: 'statusCompletedWithErrors'
  },
  failed: {
    className: 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400',
    labelKey: 'statusFailed'
  },
  skipped: {
    className: 'border-muted-foreground/30 bg-muted/10 text-muted-foreground',
    labelKey: 'statusSkipped'
  }
};

// ── Container-level QA status ──────────────────────────────────────────────
// Every page-level OcrResult carries its own qa_stage, but a container is
// always submitted as a single unit. We pick the *least advanced* stage so
// the badge reflects what's still in flight (e.g. one page already in tier 2
// while another sits in tier 1 → show tier 1). `qa_completed` only wins when
// every page is done; any `qa_rejected` short-circuits to rejected.
const QA_STAGE_ORDER: OcrQaStage[] = [
  'pending_qa1',
  'in_qa1',
  'pending_qa2',
  'in_qa2',
  'pending_qa3',
  'in_qa3',
  'qa_completed'
];

interface QaStatusView {
  label: string;
  className: string;
  icon: typeof CheckCircle2;
  spin?: boolean;
}

function getContainerQaStage(results: OcrResult[]): OcrQaStage | null {
  if (results.length === 0) return null;
  const stages = results
    .map((r) => r.qa_stage)
    .filter((s): s is OcrQaStage => s != null);
  if (stages.length === 0) return null;
  if (stages.some((s) => s === 'qa_rejected')) return 'qa_rejected';
  let best: OcrQaStage = 'qa_completed';
  let bestIdx = QA_STAGE_ORDER.indexOf(best);
  for (const s of stages) {
    const idx = QA_STAGE_ORDER.indexOf(s);
    if (idx >= 0 && idx < bestIdx) {
      best = s;
      bestIdx = idx;
    }
  }
  return best;
}

function getQaStatusView(
  stage: OcrQaStage | null,
  t: ReturnType<typeof useTranslations>
): QaStatusView {
  if (stage == null) {
    return {
      label: t('qaStatusNotSent'),
      className: 'border-muted-foreground/30 bg-muted text-muted-foreground',
      icon: Clock
    };
  }
  if (stage === 'qa_completed') {
    return {
      label: t('qaStatusCompleted'),
      className:
        'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
      icon: ShieldCheck
    };
  }
  if (stage === 'qa_rejected') {
    return {
      label: t('qaStatusRejected'),
      className:
        'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400',
      icon: XCircle
    };
  }
  const tier = Number(stage.slice(-1));
  const isPending = stage.startsWith('pending');
  return {
    label: isPending
      ? t('qaStatusPendingTier', { tier })
      : t('qaStatusInTier', { tier }),
    className: isPending
      ? 'border-yellow-500/30 bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
      : 'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400',
    icon: isPending ? Clock : Loader2,
    spin: !isPending
  };
}

// Stable identity for a single extracted field across its result + page. The
// `key` alone isn't unique (repeating templates reuse it per page), so pair it
// with the result id and page number. Used to track which fields have an
// accept request in flight for per-field spinners.
function fieldUid(
  resultId: number,
  key: string,
  pageNumber?: number | null
): string {
  return `${resultId}::${key}::${pageNumber ?? ''}`;
}

// ── Whole-page transcription (OMA-616) ─────────────────────────────────────

/**
 * The page's raw text, open by default, collapsible, and CORRECTABLE.
 *
 * It is still not a verifiable field — no highlight to check a value against,
 * no accept affordance, and it can never reach `processed` (the backend
 * refuses that transition for a `full_page` item even on a manual edit, and
 * three counters depend on it). But "cannot be ACCEPTED" was never the same
 * claim as "cannot be FIXED": the model mistranscribes handwritten Arabic, and
 * the dump is what the search index and any downstream reader see, so an
 * operator has to be able to correct it.
 *
 * Editing routes through the same `onUpdateField` every other value uses, so
 * a QA reviewer's correction goes to their tier-scoped endpoint rather than
 * the operator's, and `readOnly` closes it on the surfaces that may not write.
 *
 * It is also the tallest thing in the panel and one renders per page, so
 * folding it away stays possible. Collapse and edit state are both per section
 * instance — each page's dump folds and edits on its own.
 */
function FullPageContentSection({
  fields,
  renderKey,
  resultId,
  readOnly,
  onUpdateField
}: {
  fields: ExtractedDataItem[];
  /** Builds the per-item React key — the same `key` repeats across pages. */
  renderKey: (field: ExtractedDataItem) => string;
  resultId: number;
  readOnly: boolean;
  onUpdateField: (
    resultId: number,
    key: string,
    value: string,
    pageNumber?: number | null
  ) => Promise<void> | void;
}) {
  const t = useTranslations('ocr');
  const [open, setOpen] = useState(true);
  // Keyed by the item's render key rather than a lone boolean: a page group can
  // in principle carry more than one whole-page item, and a shared flag would
  // put every one of them into edit mode at once.
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = (key: string, value: ExtractedDataItem['value']) => {
    setEditingKey(key);
    setDraft(value == null ? '' : String(value));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraft('');
  };

  const saveEdit = async (field: ExtractedDataItem) => {
    setSaving(true);
    try {
      await onUpdateField(resultId, field.key, draft, field.page_number);
      setEditingKey(null);
      setDraft('');
    } catch {
      // Surfaced by the global MutationCache; the draft stays on screen so a
      // failed save does not discard what the operator just typed.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen} className='space-y-2 pt-2'>
      {/* The separator-flanked heading the other sections use, made the
          trigger — so the section reads the same open or closed. */}
      <CollapsibleTrigger className='group flex w-full items-center gap-2'>
        <Separator className='flex-1' />
        <span className='text-muted-foreground group-hover:text-foreground flex items-center gap-1 text-xs font-medium transition-colors'>
          {t('fullPageSectionTitle')}
          {/* Points at what the click does: down to expand, up to fold away.
              `group-data-[state=open]` and NOT `[[data-state=open]_&]`: this
              section is nested inside the page card, which is itself an open
              Collapsible, so an unscoped ancestor selector matched the CARD's
              state and left the chevron rotated in both states. `group` on the
              trigger pins it to this section's own `data-state`. */}
          <ChevronDown className='h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180' />
        </span>
        <Separator className='flex-1' />
      </CollapsibleTrigger>

      <CollapsibleContent className='space-y-2'>
        {fields.map((field) => {
          const key = renderKey(field);
          const editing = editingKey === key;

          return (
            <div key={key} className='space-y-1.5'>
              {editing ? (
                <>
                  <Textarea
                    // Same reason as the read-only dump below: many
                    // independent lines, each resolving its own direction.
                    dir='auto'
                    // `className` sits before `onChange` deliberately.
                    // `ocr-value-bidi.test.ts` reads the opening tag up to its
                    // first closing angle bracket, and an inline arrow handler
                    // contains one — so a bidi class placed after such a
                    // handler is invisible to the guard that exists to keep it
                    // there. Keep both out of this comment for the same reason.
                    className='max-h-96 min-h-48 text-xs [unicode-bidi:plaintext]'
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    autoFocus
                    disabled={saving}
                    onKeyDown={(e) => {
                      // Enter must insert a newline here — this is prose, and
                      // a page of it. Only the modifier chord saves.
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        void saveEdit(field);
                      }
                      if (e.key === 'Escape') cancelEdit();
                    }}
                  />
                  <div className='flex items-center justify-end gap-1.5'>
                    <Button
                      size='sm'
                      variant='ghost'
                      className='h-7 gap-1.5 text-xs'
                      onClick={cancelEdit}
                      disabled={saving}
                    >
                      <X className='h-3.5 w-3.5' />
                      {t('cancel')}
                    </Button>
                    <Button
                      size='sm'
                      className='h-7 gap-1.5 text-xs'
                      onClick={() => void saveEdit(field)}
                      disabled={saving}
                    >
                      {saving ? (
                        <Loader2 className='h-3.5 w-3.5 animate-spin' />
                      ) : (
                        <Check className='h-3.5 w-3.5' />
                      )}
                      {t('saveFieldHint')}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <pre
                    // A page dump is many independent lines in the document's
                    // language, so each has to resolve its own direction —
                    // otherwise the RTL shell reorders every Latin line it
                    // contains.
                    dir='auto'
                    className='bg-muted/40 max-h-72 overflow-auto rounded-lg border p-3 text-xs whitespace-pre-wrap [unicode-bidi:plaintext]'
                  >
                    {field.value}
                  </pre>
                  {!readOnly && (
                    <div className='flex items-center justify-end'>
                      <Button
                        size='sm'
                        variant='ghost'
                        className='h-7 gap-1.5 text-xs'
                        onClick={() => startEdit(key, field.value)}
                      >
                        <Pencil className='h-3.5 w-3.5' />
                        {t('editFullPageContent')}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
}

// ── Page fields section ────────────────────────────────────────────────────

function PageFieldsSection({
  result,
  fields,
  pageLabel,
  pageNumber,
  templateName,
  open,
  onOpenChange,
  activeField,
  hoveredField,
  highlightsEnabled,
  onFieldHover,
  onFieldClick,
  onAcceptField,
  onUpdateField,
  onRenameField,
  onAcceptAllPage,
  onRerunPage,
  onAddField,
  addFieldDisabled,
  operatorTools,
  acceptingFieldIds,
  allowEditVerified,
  readOnly,
  fieldColorOverrides,
  defaultHighlightColor,
  onFieldColorChange,
  onAddHighlight,
  drawingFieldKey,
  renderFooter,
  renderFieldAction
}: {
  result: OcrResult;
  fields: ExtractedDataItem[];
  pageLabel: string;
  /** 1-based document-global page this section renders. */
  pageNumber: number;
  /** Shown when the unified all-templates view needs per-section attribution. */
  templateName?: string | null;
  /** Exposes the edit affordance on already-verified fields (QA detail only). */
  allowEditVerified?: boolean;
  /** Viewing a frozen history snapshot — hides accept/edit/"accept all". */
  readOnly?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeField: FieldId | null;
  hoveredField: FieldId | null;
  highlightsEnabled: boolean;
  /** Field uids (see `fieldUid`) whose accept request is currently in flight. */
  acceptingFieldIds: ReadonlySet<string>;
  onFieldHover: (field: FieldId | null) => void;
  onFieldClick: (field: FieldId) => void;
  /** Arms draw-a-highlight for a field with no region yet (OMA-592). */
  onAddHighlight?: (
    resultId: number,
    key: string,
    pageNumber?: number | null
  ) => void;
  /** Field key currently being drawn for, so its button reads as pressed. */
  drawingFieldKey?: string | null;
  onAcceptField: (
    resultId: number,
    key: string,
    pageNumber?: number | null
  ) => void;
  onUpdateField: (
    resultId: number,
    key: string,
    value: string,
    pageNumber?: number | null
  ) => void;
  /**
   * Rename a field (OMA-631) — the NAME, not the value, and result-local. The
   * promise is what the card awaits: a refused name (folds to nothing, or is
   * already carried by another field on the page) has to leave the inline
   * editor open. Absent hides the affordance, which is how the QA and
   * supervisor surfaces render — renaming is an operator edit.
   */
  onRenameField?: (
    resultId: number,
    key: string,
    name: string,
    pageNumber?: number | null
  ) => Promise<unknown>;
  onAcceptAllPage: (
    resultId: number,
    pageNumber: number,
    fields: Array<{ key: string; pageNumber?: number | null }>
  ) => void;
  /**
   * Re-extract just this page. Only wired for a container returned from QA —
   * absent means the affordance is hidden.
   */
  onRerunPage?: (resultId: number, pageNumber: number) => void;
  /**
   * Add a field to THIS page with no highlight (OMA-630 follow-up).
   *
   * `pageNumber` is already result-local (`pageIndexInResult`), which is the
   * space the create endpoint stores — so unlike the toolbar's draw flow there
   * is no viewer index to map back. Absent hides the affordance: a section that
   * belongs to no page (the unattributed bucket) has no page to file against.
   */
  onAddField?: (resultId: number, pageNumber: number) => void;
  /**
   * The container-level add-field flow is mid-run (a draft is armed waiting for
   * a drag, or the whole run is in flight). Offering a second entry point into
   * the same dialog would make one draft mean two things.
   */
  addFieldDisabled?: boolean;
  /**
   * Whether the OCR-OPERATOR tools may render here. Distinct from `readOnly`,
   * which is about field values: a QA reviewer edits values through their own
   * tier endpoint, but must never see re-run, accept-a-whole-page, or the
   * highlight/colour tools. Collapsing the two is what put the operator's
   * whole toolbar on the QA screen.
   */
  operatorTools: boolean;
  /** Per-document highlight overrides, keyed by extracted-field `key`. */
  fieldColorOverrides: Record<string, string>;
  /** `ocr_default_highlight_color` — used when a field resolves no color. */
  defaultHighlightColor: string;
  /** Recolor a field for this document only; absent hides the swatch. */
  onFieldColorChange?: (fieldKey: string, color: string | null) => void;
  renderFooter?: () => ReactNode;
  /**
   * Optional slot rendered directly UNDER one field's card.
   *
   * Distinct from `renderFooter`, which renders once per page section: the
   * supervisor's discovered-field verdicts belong on the field they decide,
   * beside the value and the region it came from. Returning null for a field
   * renders nothing, so a surface can opt in for some fields only — the
   * supervisor's does exactly that, offering a verdict on free-form fields
   * and nothing on declared ones.
   */
  renderFieldAction?: (field: ExtractedDataItem) => ReactNode;
}) {
  const t = useTranslations('ocr');
  // The whole-page item (OMA-616) is not verifiable — it has no highlight and
  // no accept affordance — so it counts towards neither side of the ratio.
  const verifiableFields = fields.filter((f) => f.source !== 'full_page');
  const verifiedCount = verifiableFields.filter(
    (f) => f.status === 'processed'
  ).length;

  // A per-page re-run flips the WHOLE result to `processing` even though only
  // the listed pages are actually in flight. Scope the extraction timeline and
  // the disabled-controls state to those pages, so the rest of the container
  // stays readable and acceptable instead of looking wiped. An empty list means
  // a normal full run, where every page really is in flight.
  // A page left out of the re-run is still whatever it was before the re-run
  // started, so it must not inherit the parent's transient `processing` badge;
  // likewise only the pages in `failed_page_ranges` read as failed.
  const isProcessing = isPageInFlight(result, pageNumber);
  const isPageRerunning = (result.rerunning_page_numbers ?? []).includes(
    pageNumber
  );
  const effectiveStatus: DisplayOcrStatus = pageDisplayStatus(
    result,
    pageNumber
  );
  const statusBadge = STATUS_BADGE[effectiveStatus];
  const stepStates = getStepStates(effectiveStatus, fields.length > 0);
  // Any field of this page with an in-flight accept — drives the page-level
  // "Accept all" button spinner.
  const isAcceptingPage = fields.some((f) =>
    acceptingFieldIds.has(fieldUid(result.id, f.key, f.page_number))
  );

  // Stable per-section callbacks. Without these, the field cards see new
  // function references every render and `memo` on ExtractedFieldCard would
  // re-render all of them on every hover — defeating the optimization.
  const resultId = result.id;
  const handleHover = useCallback(
    (key: string | null, ref: string) => {
      if (!highlightsEnabled) return;
      onFieldHover(key ? { resultId, key, ref } : null);
    },
    [highlightsEnabled, onFieldHover, resultId]
  );
  const handleClick = useCallback(
    (key: string, ref: string) => {
      if (!highlightsEnabled) return;
      onFieldClick({ resultId, key, ref });
    },
    [highlightsEnabled, onFieldClick, resultId]
  );
  const handleAccept = useCallback(
    (key: string, pageNumber?: number | null) => {
      onAcceptField(resultId, key, pageNumber);
    },
    [onAcceptField, resultId]
  );
  const handleUpdate = useCallback(
    (key: string, value: string, pageNumber?: number | null) => {
      onUpdateField(resultId, key, value, pageNumber);
    },
    [onUpdateField, resultId]
  );

  // Returns the promise rather than firing and forgetting: the card keeps its
  // editor open on a rejection, so it has to be able to await the write.
  const handleRename = useCallback(
    (key: string, name: string, pageNumber?: number | null) =>
      onRenameField
        ? onRenameField(resultId, key, name, pageNumber)
        : Promise.resolve(),
    [onRenameField, resultId]
  );

  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <CollapsibleTrigger className='hover:bg-muted/50 flex w-full items-center justify-between border-b px-5 py-3 transition-colors'>
        <div className='flex items-center gap-2'>
          <span className='text-sm font-medium'>{pageLabel}</span>
          {templateName ? (
            <Badge
              variant='secondary'
              className='flex max-w-40 items-center gap-1 text-[10px]'
              title={templateName}
            >
              <span className='truncate'>{templateName}</span>
              <TemplateFieldsInfo templateId={result.ocr_template_id} />
            </Badge>
          ) : null}
          <Badge
            variant='outline'
            className={cn('text-[10px]', statusBadge.className)}
          >
            {t(statusBadge.labelKey)}
          </Badge>
          {verifiableFields.length > 0 && (
            <span className='text-muted-foreground text-xs'>
              {verifiedCount}/{verifiableFields.length}
            </span>
          )}
        </div>
        <ChevronDown className='text-muted-foreground h-4 w-4 transition-transform [[data-state=open]_&]:rotate-180' />
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className='px-3 py-3'>
          {/* Processing indicator */}
          {isProcessing && fields.length === 0 && (
            <div className='space-y-3 py-4'>
              <ExtractionTimeline
                stepStates={stepStates}
                currentPage={result.current_page}
                totalPages={result.total_pages}
                progressStage={result.progress_stage}
              />
              <div className='flex flex-col items-center py-4'>
                <motion.div
                  animate={{ scale: [1, 1.06, 1] }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                  className='bg-primary/5 mb-3 rounded-xl p-3'
                >
                  <ScanText className='text-primary/40 h-6 w-6' />
                </motion.div>
                <p className='text-muted-foreground text-xs'>
                  {t('noFieldsYet')}
                </p>
              </div>
            </div>
          )}

          {/* Processing with early fields */}
          {isProcessing && fields.length > 0 && (
            <div className='mb-3'>
              <ExtractionTimeline
                stepStates={stepStates}
                currentPage={result.current_page}
                totalPages={result.total_pages}
                progressStage={result.progress_stage}
              />
            </div>
          )}

          {/* Failed state — the whole run, or just this page of it */}
          {effectiveStatus === 'failed' && (
            <div className='py-3'>
              <OcrErrorCard
                resultId={result.id}
                errorCode={result.error_code}
                errorMessage={result.error_message}
              />
            </div>
          )}

          {/* Skipped (duplicate) state */}
          {result.status === 'skipped' && (
            <div className='text-muted-foreground flex items-center gap-2 py-4 text-center'>
              <Copy className='h-4 w-4' />
              <p className='text-xs'>
                {result.error_message ?? t('statusSkipped')}
              </p>
            </div>
          )}

          {/* Field cards */}
          {fields.length > 0 && (
            <div className='space-y-2'>
              {/* Per-page actions: re-extract just this page, accept all of it */}
              {operatorTools && result.status !== 'failed' && (
                <div className='flex items-center justify-end gap-1'>
                  {onRerunPage && (
                    <Button
                      size='sm'
                      variant='ghost'
                      className='h-7 text-xs'
                      disabled={isPageRerunning}
                      onClick={() => onRerunPage(result.id, pageNumber)}
                    >
                      <RotateCcw
                        className={cn(
                          'me-1 h-3 w-3',
                          isPageRerunning && 'animate-spin'
                        )}
                      />
                      {isPageRerunning
                        ? t('rerunningThisPage')
                        : t('rerunThisPage')}
                    </Button>
                  )}
                  {!isProcessing && verifiedCount < verifiableFields.length && (
                    <Button
                      size='sm'
                      variant='ghost'
                      className='h-7 text-xs'
                      disabled={isAcceptingPage}
                      onClick={() =>
                        onAcceptAllPage(
                          result.id,
                          pageNumber,
                          fields
                            .filter(
                              (f) =>
                                f.status !== 'processed' &&
                                f.source !== 'full_page'
                            )
                            .map((f) => ({
                              key: f.key,
                              pageNumber: f.page_number
                            }))
                        )
                      }
                    >
                      {isAcceptingPage ? (
                        <Loader2 className='me-1 h-3 w-3 animate-spin' />
                      ) : (
                        <CheckCheck className='me-1 h-3 w-3' />
                      )}
                      {t('acceptAll')}
                    </Button>
                  )}
                  {/* Add a field this page is missing, with NO region (OMA-630
                      follow-up). The toolbar's button arms a drag on the
                      document; this one saves straight away, because the value
                      an operator is transcribing here is one the model gave no
                      geometry for anyway — the field lands ungrounded and its
                      card offers "draw a highlight" like any other. Disabled
                      while the page is in flight: its fields are stale
                      server-side until the re-extraction merges back. */}
                  {onAddField && (
                    <Button
                      size='sm'
                      variant='ghost'
                      className='h-7 text-xs'
                      disabled={addFieldDisabled || isProcessing}
                      onClick={() => onAddField(result.id, pageNumber)}
                    >
                      <Plus className='me-1 h-3 w-3' />
                      {t('addField')}
                    </Button>
                  )}
                </div>
              )}

              {(() => {
                const { template, freeform, fullPage } =
                  partitionFields(fields);
                const renderField = (field: ExtractedDataItem, i: number) => (
                  <motion.div
                    // `${key}-${page}` is NOT unique: one page can hold the
                    // same field key twice (a repeating occurrence, or a
                    // free-form item that rediscovered a declared name), which
                    // React reports as two children sharing a key. The chunk
                    // reference is what tells the two apart.
                    key={`${field.key}-${field.page_number ?? 0}-${fieldRef(field) || i}`}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.3,
                      delay: Math.min(i * 0.04, 0.4),
                      ease: 'easeOut'
                    }}
                  >
                    <ExtractedFieldCard
                      field={field}
                      isActive={
                        highlightsEnabled &&
                        sameField(activeField, result.id, field)
                      }
                      isHighlighted={
                        highlightsEnabled &&
                        sameField(hoveredField, result.id, field)
                      }
                      highlightsEnabled={highlightsEnabled}
                      isAccepting={acceptingFieldIds.has(
                        fieldUid(result.id, field.key, field.page_number)
                      )}
                      allowEditVerified={allowEditVerified}
                      readOnly={readOnly}
                      onHover={handleHover}
                      onClick={handleClick}
                      onAccept={handleAccept}
                      onUpdate={handleUpdate}
                      onRename={onRenameField ? handleRename : undefined}
                      highlightColor={resolveFieldHighlightColor(
                        fieldColorOverrides[field.key],
                        field.color,
                        defaultHighlightColor
                      )}
                      hasColorOverride={fieldColorOverrides[field.key] != null}
                      onColorChange={onFieldColorChange}
                      onAddHighlight={
                        onAddHighlight
                          ? (key, pageNumber) =>
                              onAddHighlight(result.id, key, pageNumber)
                          : undefined
                      }
                      isDrawingHighlight={drawingFieldKey === field.key}
                    />
                    {renderFieldAction?.(field)}
                  </motion.div>
                );

                return (
                  <>
                    {template.map(renderField)}

                    {/* OMA-614 — nothing rendered when the page had no extras. */}
                    {freeform.length > 0 && (
                      <div className='space-y-2 pt-2'>
                        <div className='flex items-center gap-2'>
                          <Separator className='flex-1' />
                          <span className='text-muted-foreground text-xs font-medium'>
                            {t('freeformSectionTitle')}
                          </span>
                          <Separator className='flex-1' />
                        </div>
                        <p className='text-muted-foreground text-xs'>
                          {t('freeformSectionHint')}
                        </p>
                        {freeform.map(renderField)}
                      </div>
                    )}

                    {/* OMA-616 — the whole page's text, no bbox to highlight,
                        but correctable: it feeds the search index and any
                        downstream reader, and the model mistranscribes
                        handwriting. `readOnly` is the panel's own flag, so a
                        surface that may not write values may not rewrite this
                        either. */}
                    {fullPage.length > 0 && (
                      <FullPageContentSection
                        fields={fullPage}
                        renderKey={(field) =>
                          `${field.key}-${field.page_number ?? 0}`
                        }
                        resultId={result.id}
                        // `PageFieldsSection.readOnly` is optional and absent
                        // means editable — the same default the field cards
                        // above take. The real gate is the panel's
                        // `readOnly={!capabilities.fieldsEditable}`, which is
                        // always passed; this only keeps the new prop
                        // non-optional so a future caller cannot omit it.
                        readOnly={readOnly ?? false}
                        onUpdateField={onUpdateField}
                      />
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Completed: no fields */}
          {result.status === 'completed' && fields.length === 0 && (
            <p className='text-muted-foreground py-4 text-center text-xs'>
              {t('noExtractedData')}
            </p>
          )}

          {/* Duration */}
          {result.duration_ms != null && (
            <p className='text-muted-foreground mt-2 text-center text-[10px]'>
              {t('processedIn', {
                duration: formatDuration(result.duration_ms)
              })}
            </p>
          )}

          {renderFooter?.()}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

interface OcrResultsPageProps {
  containerId: number;
  /**
   * Optional slot rendered at the bottom of each page section.
   *
   * `pageIndexInResult` is 1-based and names the PAGE this section covers, not
   * this section's ordinal within the result — a result whose chunks start on
   * page 2 has no section numbered 1. Anything that must render once per
   * result (a verdict, a per-result action) has to key off
   * `isFirstGroupForResult` instead; comparing `pageIndexInResult` to 1 silently
   * skips every result that doesn't own page 1.
   */
  renderPageFooter?: (
    result: OcrResult,
    pageIndexInResult: number,
    isFirstGroupForResult: boolean
  ) => ReactNode;
  /** Optional slot rendered at the bottom of the right panel (below all page sections). */
  renderPanelFooter?: () => ReactNode;
  /**
   * Optional slot rendered under one field's card, for a decision that belongs
   * to the FIELD rather than to the page or the container — the supervisor's
   * discovered-field verdicts. Return null to render nothing for a field.
   */
  renderFieldAction?: (
    result: OcrResult,
    field: ExtractedDataItem
  ) => ReactNode;
  /**
   * Restrict the panel to fields of these provenances. Omitted = all of them.
   *
   * Applied at `sortedResults`, the single root every derived value reads, so
   * the page sections, the per-page counters, the panel search AND the
   * document's highlight overlays all narrow together. Filtering at render
   * instead would leave the viewer outlining regions for fields the panel no
   * longer lists, and a page header counting fields nobody can see.
   *
   * The supervisor's review passes `['freeform']`: it exists to decide
   * DISCOVERED fields, and the template's own extractions are noise that
   * pushes the thing being reviewed below the fold.
   */
  fieldSources?: readonly ExtractedFieldSource[];
  /**
   * Narrow the panel further to fields with these keys, at the same root as
   * `fieldSources`. The supervisor's review passes one key when it is opened
   * from a field's "Files" list, so the reviewer sees that field alone.
   * Pass a stable array — the memo is keyed on identity.
   */
  fieldKeys?: readonly string[];
  /**
   * Exposes the edit affordance on already-verified fields (QA detail pages
   * only). Off by default so verified fields stay final on the OCR page.
   */
  allowEditVerified?: boolean;
  /**
   * Overrides the field-update handler. QA detail supplies this so edits route
   * through the QA-scoped endpoint (`qa{tier}_ocr.edit`) instead of the default
   * `ocr.edit` mutation. Must preserve optimistic-update behavior itself.
   */
  onUpdateFieldOverride?: (
    resultId: number,
    key: string,
    value: string,
    pageNumber?: number | null
  ) => Promise<void> | void;
  /**
   * Enables manual drag-resize/move of chunk bounding boxes in the viewer.
   * Defaults to on; the QA detail page opts out explicitly since box editing
   * is out of scope for the QA review flow (QA-tier permissions also lack
   * `ocr.edit`, so this would 403 anyway — but this keeps it out
   * deliberately rather than relying on the 403 as the only guard).
   */
  enableBboxEditing?: boolean;
  /**
   * Which screen is rendering the panel. Required on purpose: the QA detail
   * page used to render this with no editing prop at all and silently
   * inherited the operator surface — re-run OCR, re-run page, accept-all,
   * submit-to-QA, draw highlight and the colour picker all appeared on a
   * reviewer's screen. See `resolvePanelCapabilities`.
   */
  surface: OcrPanelSurface;
  /**
   * QA surface only: whether the container is actionable at the tier that is
   * rendering. The panel cannot know its own tier, so the QA screen — which
   * already computes this for its verdict buttons — states it.
   */
  qaTierActive?: boolean;
  /**
   * Page/field to land on, supplied by a host that owns the fragment itself.
   *
   * The panel normally reads its target straight off the hash, which works
   * while it IS the route. Rendered inside the OCR screen's results overlay
   * (OMA-635) the fragment belongs to the overlay — `#results-view/<id>/…` —
   * so the host parses the target and states it here instead. Omitted on the
   * standalone route, which keeps reading the hash exactly as before.
   */
  deepLink?: ResultsDeepLink | null;
}

export function OcrResultsPage({
  containerId,
  renderPageFooter,
  renderPanelFooter,
  renderFieldAction,
  fieldSources,
  fieldKeys,
  allowEditVerified,
  onUpdateFieldOverride,
  enableBboxEditing = true,
  surface,
  qaTierActive,
  deepLink
}: OcrResultsPageProps) {
  const t = useTranslations('ocr');
  const router = useRouter();
  const highlightsEnabled = useOcrHighlightsEnabled();
  const defaultHighlightColor = useOcrDefaultHighlightColor();
  // Per-document highlight recolors. Client-only and scoped to this container —
  // never written back to the template's field color (see the hook's docblock).
  const { colors: fieldColorOverrides, setFieldColor } =
    useFieldHighlightColors(containerId);
  // 1024, not the default 768: below it there isn't room for the document
  // viewer next to even the extraction panel's minimum resizable width, so
  // the panel must drop into the Sheet the same way it does on phones.
  const isMobile = useIsMobile(1024);
  const extractionPanelResize = usePanelWidth('ocr-extraction', {
    defaultWidth: 420,
    min: 300,
    max: 720,
    // Sits at the row's inline end, so its handle is on its inline-start
    // edge — physically left in LTR, right in RTL.
    edge: 'start'
  });
  const [extractionSheetOpen, setExtractionSheetOpen] = useState(false);
  // ── Unsaved highlight edits (OMA-592 / OMA-593) ──
  // Moves, drawn boxes and removals are all held as one draft until Save, so
  // the viewer can render the pending state, undo/redo works across all three,
  // and leaving the page can warn.
  const {
    draft: highlightDraft,
    apply: applyHighlightDraft,
    undo: undoHighlightDraft,
    redo: redoHighlightDraft,
    reset: resetHighlightDraft,
    canUndo: canUndoHighlight,
    canRedo: canRedoHighlight,
    isDirty: hasUnsavedHighlights
  } = useHighlightDraft();

  // Committed overrides with the draft's pending recolors laid on top, so the
  // viewer and the field cards preview a recolor before it is saved. A pending
  // `null` means the operator reset the field back to its template color.
  const effectiveFieldColors = useMemo(() => {
    const merged: Record<string, string> = { ...fieldColorOverrides };
    for (const [key, color] of Object.entries(highlightDraft.colors)) {
      if (color == null) delete merged[key];
      else merged[key] = color;
    }
    return merged;
  }, [fieldColorOverrides, highlightDraft.colors]);
  // Which field new boxes attach to. Stays armed after a draw so several
  // regions can be added to one value without re-arming each time.
  const [drawTarget, setDrawTarget] = useState<{
    resultId: number;
    fieldKey: string;
    fieldPageNumber: number | null;
  } | null>(null);
  // ── Add a field by hand (OMA-630) ──
  // The dialog collects name/value/colour, then hands the draft here and the
  // operator draws its region. Held OUTSIDE the highlight draft on purpose:
  // that draft edits regions of fields that already exist, while this has no
  // field to attach to until the request that creates one succeeds — there is
  // nothing for undo/redo to put back.
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  // Which page section opened the dialog, or null for the toolbar's draw flow.
  // Deliberately NOT folded into `addFieldOpen`: it outlives the close so the
  // dialog's primary button keeps its label through the exit animation, and
  // every open sets it, so a stale target is never read.
  const [addFieldTarget, setAddFieldTarget] = useState<{
    resultId: number;
    pageNumber: number;
  } | null>(null);
  const [pendingManualField, setPendingManualField] =
    useState<ManualFieldDraft | null>(null);
  // Needed for the per-page re-run gate, which is passed down as a prop and so
  // can't go through PermissionGate.
  const { user } = useAuth();

  // ── Data fetching with WebSocket ──
  const { data, isPending } = useContainerResultsLive(containerId);

  // History preview: paging to a past suggestion run in the "السجل" popover
  // sets this to that run's frozen snapshot, and every derived value below
  // (pool/panel/viewer/highlights) reads it in place of the live results —
  // a read-only "viewing history" mode. `null` = live, editable view.
  const [previewRun, setPreviewRun] = useState<OcrSuggestionRunDetail | null>(
    null
  );
  const isPreviewMode = previewRun != null;

  // Sort by page number ascending (handles "PG-001" / "PG-002" format).
  // Keep skipped (duplicates) so users see what happened.
  const sortedResults = useMemo(() => {
    const results = (
      previewRun ? (previewRun.ocr_results ?? []) : (data?.data ?? [])
    ) as OcrResult[];
    const pageOrder = (p: string | null): number => {
      if (!p) return Number.POSITIVE_INFINITY;
      const match = p.match(/\d+/);
      return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
    };
    const ordered = [...results].sort(
      (a, b) => pageOrder(a.page_number) - pageOrder(b.page_number)
    );

    // A missing `source` means `template` — every row stored before OMA-614 is
    // a template extraction, so absence must not slip through a
    // `freeform`-only filter. See `narrowResultFields`.
    return narrowResultFields(ordered, {
      sources: fieldSources,
      keys: fieldKeys
    });
  }, [data, previewRun, fieldSources, fieldKeys]);

  // ── Multi-template pool run ──
  // Pool siblings share a template_pool_run_token. The unified view shows
  // every sibling's extraction together (template filter defaults to "all");
  // per-page "best match" hints come from winner_template_id_per_page,
  // computed server-side once every sibling finishes.
  const pool = useMemo(() => {
    const byToken = new Map<string, OcrResult[]>();
    for (const r of sortedResults) {
      if (!r.template_pool_run_token) continue;
      const arr = byToken.get(r.template_pool_run_token) ?? [];
      arr.push(r);
      byToken.set(r.template_pool_run_token, arr);
    }
    let latest: OcrResult[] | null = null;
    for (const siblings of byToken.values()) {
      if (siblings.length < 2) continue;
      const maxId = Math.max(...siblings.map((s) => s.id));
      if (!latest || maxId > Math.max(...latest.map((s) => s.id))) {
        latest = siblings;
      }
    }
    return latest;
  }, [sortedResults]);

  // Template filter for the extraction panel: 'all' shows every sibling's
  // data together; a result id narrows to that template only.
  const [templateFilter, setTemplateFilter] = useState<'all' | number>('all');

  // Accepted/rejected pages filter — only shown (and applied) once the
  // container has a QA rejection, letting the operator focus on just the
  // pages that need fixing. Defaults to "rejected" since that's the
  // actionable state for an operator who just landed on a rejected
  // container. Independent of `templateFilter` — a separate, local-only
  // control, not persisted anywhere.
  // Defaults to `rejected` — on a returned container that is the work list.
  // `all` exists because without it the operator can only ever see one bucket
  // and never the container's full page list.
  const [pageAcceptanceFilter, setPageAcceptanceFilter] = useState<
    'all' | 'accepted' | 'rejected' | 'pending'
  >('rejected');

  // A result id filtered on the live view (or a different historical run)
  // won't match this run's own result ids — reset on every preview target
  // change (including back to live) so the filter never points at nothing.
  useEffect(() => {
    setTemplateFilter('all');
  }, [previewRun?.id]);

  // The winner map is identical on every sibling — read it from whichever
  // result carries it (null until the selector job has run).
  const poolWinnerMap = useMemo(
    () =>
      pool?.find((r) => r.winner_template_id_per_page)
        ?.winner_template_id_per_page ?? null,
    [pool]
  );

  const poolWinCounts = useMemo(() => {
    const counts = new Map<number, number>();
    if (poolWinnerMap) {
      for (const templateId of Object.values(poolWinnerMap)) {
        counts.set(templateId, (counts.get(templateId) ?? 0) + 1);
      }
    }
    return counts;
  }, [poolWinnerMap]);

  // Template that best-matched the most pages — gets the trophy in the filter.
  const topWinnerTemplateId = useMemo(() => {
    let bestId: number | null = null;
    let bestCount = 0;
    for (const [templateId, count] of poolWinCounts) {
      if (count > bestCount) {
        bestCount = count;
        bestId = templateId;
      }
    }
    return bestId;
  }, [poolWinCounts]);

  // Fields each sibling resolved to NO page, keyed by result id. Same
  // exclusion the backend applies when scoring page winners, so a high count
  // means that candidate template extracted values the pool run never credited
  // to any page — the signal for dropping it before paying for another run.
  // Computed off `pool` (not `pageGroups`) so the numbers stay comparable
  // while the panel is filtered to a single template.
  const poolUnattributedCounts = useMemo(() => {
    const counts = new Map<number, number>();
    for (const r of pool ?? []) {
      const chunkPage = new Map(
        (r.chunks ?? []).map((c) => [c.chunk_id, c.page_number])
      );
      const { unattributed } = bucketFieldsByPage(
        r.extracted_data ?? [],
        chunkPage
      );
      counts.set(r.id, unattributed.length);
    }
    return counts;
  }, [pool]);

  // OMA-626 — built from EVERY result, not just `pool`. Keyed off `pool` it
  // was populated only for a multi-template run (>= 2 siblings), so a
  // single-template container had an empty map and `PageFieldsSection` — which
  // takes a `templateName` and renders it — showed no template badge at all.
  // That is the whole of "show the template name inside OCR" for the page
  // sections, and it lands on the QA and supervisor surfaces for free because
  // all three render this panel. The pool-only consumers (the template filter
  // Select, the cluster labels) iterate `pool` themselves and are unaffected
  // by the extra entries.
  const templateNameById = useMemo(() => {
    const names = new Map<number, string>();
    for (const r of sortedResults) {
      if (r.ocr_template_id != null) {
        names.set(
          r.ocr_template_id,
          r.ocr_template?.name ?? `#${r.ocr_template_id}`
        );
      }
    }
    return names;
  }, [sortedResults]);

  // Viewer anchor: the sibling whose pages drive the LEFT document viewer.
  // Siblings all share the same physical pages, so the viewer renders one
  // copy regardless of the panel filter — the explicit pick when filtered,
  // else the template that won the most pages.
  const viewerAnchorResult = useMemo(() => {
    if (!pool) return null;
    if (templateFilter !== 'all') {
      const picked = pool.find((r) => r.id === templateFilter);
      if (picked) return picked;
    }
    return [...pool].sort(
      (a, b) =>
        (poolWinCounts.get(b.ocr_template_id ?? -1) ?? 0) -
        (poolWinCounts.get(a.ocr_template_id ?? -1) ?? 0)
    )[0];
  }, [pool, templateFilter, poolWinCounts]);

  // Results feeding the RIGHT extraction panel: all siblings in 'all' mode,
  // one sibling when filtered — plus any non-pool rows (e.g. skipped
  // duplicate-page results) either way.
  const panelResults = useMemo(() => {
    if (!pool || templateFilter === 'all') return sortedResults;
    const token = pool[0]?.template_pool_run_token;
    return sortedResults.filter(
      (r) => r.template_pool_run_token !== token || r.id === templateFilter
    );
  }, [sortedResults, pool, templateFilter]);

  // Container-level QA stage — computed early (rather than alongside the
  // other stats below) because the accepted/rejected pages filter needs it
  // to gate both the pageGroups filter step and the toggle's visibility.
  const containerQaStage = useMemo(
    () => getContainerQaStage(panelResults),
    [panelResults]
  );

  // Results feeding the LEFT viewer: exactly one pool sibling (the anchor)
  // so pages never duplicate, plus non-pool rows.
  const viewerResults = useMemo(() => {
    if (!pool || !viewerAnchorResult) return sortedResults;
    const token = viewerAnchorResult.template_pool_run_token;
    return sortedResults.filter(
      (r) =>
        r.template_pool_run_token !== token || r.id === viewerAnchorResult.id
    );
  }, [sortedResults, pool, viewerAnchorResult]);

  // Aggregated run progress across the siblings still in flight — ONE bar for
  // the whole multi-template run instead of a progress timeline per template.
  // A finished sibling beside a re-run is not part of the work in progress.
  const poolProgress = useMemo(() => {
    if (!pool) return null;
    const percent = runProgressPercent(pool);
    if (percent == null) return null;
    const rerunPages = rerunningPagesInFlight(pool);
    const inFlightCount = pool.filter(
      (r) => r.status === 'pending' || r.status === 'processing'
    ).length;
    return { percent, rerunPages, inFlightCount };
  }, [pool]);

  // ── Mutations ──
  const acceptFieldMutation = useAcceptOcrFieldMutation();
  const acceptAllMutation = useAcceptAllOcrFieldsMutation();
  const acceptAllOnPageMutation = useAcceptAllOcrFieldsOnPageMutation();
  const submitToQaMutation = useSubmitContainerToQaMutation();
  const updateFieldMutation = useUpdateOcrFieldMutation();
  const renameFieldMutation = useRenameOcrFieldMutation();
  const updateChunkBboxMutation = useUpdateChunkBboxMutation();
  const createFieldHighlightMutation = useCreateFieldHighlightMutation();
  const createOcrFieldMutation = useCreateOcrFieldMutation();
  const deleteFieldHighlightMutation = useDeleteFieldHighlightMutation();
  const rerunOcrMutation = useStartOcrMutation();
  const rerunPageMutation = useRerunOcrPageMutation();

  // ── State ──
  // Read-only here: the panel adopts a deep-linked target but never writes the
  // fragment back — the operator's own scrolling is not URL state, and echoing
  // it would spam history on every page turn.
  const { hash } = useUrlHash();
  const [activePage, setActivePage] = useState(0);
  // Where the last hover left the viewer. Separate from `activePage` because
  // that one also drives the right panel's active group, which must NOT follow
  // hover — see `handleViewerPageChange` and `utils/viewer-page.ts`.
  const [parkedViewerPage, setParkedViewerPage] = useState<number | null>(null);
  // History lives in a header popover (not a stacked bottom section) so it
  // never steals vertical space from the field list. Fetch only while open.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [activeField, setActiveField] = useState<FieldId | null>(null);
  const [hoveredField, setHoveredField] = useState<FieldId | null>(null);
  // Tracks which page-group accordions the user has explicitly collapsed.
  // Default = empty set (all expanded). Indices are positions in `pageGroups`.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(
    () => new Set()
  );
  // Field uids (see `fieldUid`) whose accept request is in flight — drives the
  // per-field and per-page "verifying…" spinners.
  const [acceptingFieldIds, setAcceptingFieldIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const markAccepting = useCallback((ids: string[], inFlight: boolean) => {
    setAcceptingFieldIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (inFlight) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);
  const pageSectionRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  // Suppresses the right-panel auto-scroll while the user is actively
  // scrolling the panel — without this the effect snaps the panel back to
  // whichever group the mouse happens to be hovering, fighting the user's
  // scroll. Mirrors the viewer's existing `isAutoScrolling` guard.
  const isUserScrollingPanel = useRef(false);
  const userScrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const markUserScrolling = useCallback(() => {
    isUserScrollingPanel.current = true;
    if (userScrollTimer.current) clearTimeout(userScrollTimer.current);
    userScrollTimer.current = setTimeout(() => {
      isUserScrollingPanel.current = false;
    }, 600);
  }, []);

  // ── Resolve pages per result (with legacy fallback) ──
  // The OCR pipeline produces a single result per container but processes
  // every page image. The backend exposes all sibling pages via `result.pages`
  // so we can render each one in the carousel and split fields by page.
  const resolvedPages = useCallback((r: OcrResult): OcrPage[] => {
    if (r.pages && r.pages.length > 0) return r.pages;
    const fallback = legacyViewerPage(r);

    return fallback ? [fallback] : [];
  }, []);

  // ── The container's physical pages, resolved ONCE ──
  // Every result exposes the same container-wide `pages` list (the backend
  // serializes the whole workload assignment onto each row), so concatenating
  // one list per result multiplied the viewer by the number of rows. Extraction
  // rows all hang off the first page, but duplicate-page accounting produces one
  // `skipped` row PER repeat — so a 16-page container with 12 repeated pages
  // rendered 13 × 16 = 208 "pages", and its page counter read "1 of 208".
  // Dedupe by page id, keeping document order: one entry per physical page.
  const containerPages = useMemo(() => {
    const seen = new Set<number>();
    const ordered: { page: OcrPage; batchId: number | null }[] = [];
    for (const r of viewerResults) {
      for (const page of resolvedPages(r)) {
        if (seen.has(page.id)) continue;
        seen.add(page.id);
        ordered.push({ page, batchId: r.batch_id ?? null });
      }
    }
    return ordered;
  }, [viewerResults, resolvedPages]);

  // ── Viewer page offsets ──
  // A result's chunk `page_number` is 1-based within ITS OWN page list, so the
  // offset of that list inside `containerPages` converts one to the other. It is
  // 0 for any result carrying the full container list (the normal case) and
  // non-zero only when results span different workload assignments.
  const viewerOffsets = useMemo(() => {
    const indexById = new Map<number, number>();
    containerPages.forEach(({ page }, index) => indexById.set(page.id, index));

    const map = new Map<number, number>();
    for (const r of viewerResults) {
      const firstPage = resolvedPages(r)[0];
      map.set(r.id, (firstPage && indexById.get(firstPage.id)) ?? 0);
    }
    return map;
  }, [viewerResults, resolvedPages, containerPages]);

  const viewerOffsetFor = useCallback(
    (r: OcrResult): number => {
      const own = viewerOffsets.get(r.id);
      if (own != null) return own;
      if (
        viewerAnchorResult &&
        r.template_pool_run_token != null &&
        r.template_pool_run_token === viewerAnchorResult.template_pool_run_token
      ) {
        return viewerOffsets.get(viewerAnchorResult.id) ?? 0;
      }
      return 0;
    },
    [viewerOffsets, viewerAnchorResult]
  );

  // ── Physical pages → viewer pages (chunk overlays attached) ──
  const viewerPages: ViewerPage[] = useMemo(() => {
    // In the unified all-templates view every page carries chunk overlays from
    // EVERY sibling, so hover/click highlighting works for fields extracted by
    // any template. Filtered to one template, only the results the panel shows
    // contribute — which `viewerResults` already is (the picked sibling plus the
    // non-pool rows).
    const chunkSources =
      pool != null && templateFilter === 'all' ? sortedResults : viewerResults;

    const referencedIds = highlightsEnabled
      ? new Set(
          chunkSources.flatMap((s) =>
            (s.extracted_data ?? []).flatMap((f) => f.references)
          )
        )
      : null;

    // Maps each referenced chunk id to its field's effective highlight
    // color (per-document override → template color → default setting), so
    // the viewer tints a chunk's overlay to match the field card instead of
    // the default red/blue active/highlighted classes. The same colors ride
    // along into the "download with highlights" PDF export, which draws
    // straight from `chunks[].color`.
    const chunkColorById = highlightsEnabled
      ? new Map<string, string>(
          chunkSources.flatMap((s) =>
            (s.extracted_data ?? []).flatMap((f) => {
              const color = resolveFieldHighlightColor(
                effectiveFieldColors[f.key],
                f.color,
                defaultHighlightColor
              );

              return f.references.map((ref) => [ref, color] as const);
            })
          )
        )
      : null;

    return containerPages.map(({ page, batchId }, index) => {
      // `page_number` is 1-based within the owning result's page list, so shift
      // it by that list's offset to compare against a container-wide index.
      const pageChunks = chunkSources.flatMap((s) =>
        (s.chunks ?? []).filter(
          (c) => c.page_number === index - viewerOffsetFor(s) + 1
        )
      );

      const visibleChunks = referencedIds
        ? pageChunks
            .filter((c) => referencedIds.has(c.chunk_id))
            .map((c) => ({
              ...c,
              color: chunkColorById?.get(c.chunk_id) ?? null
            }))
        : [];

      // Page bytes are served by a permissioned API route. Pull them
      // through the authenticated blob cache so the viewer can render an
      // <img src> without exposing the raw storage URL (private files).
      // Local disk emits `image_url: null` (StorageUrl::signed only signs
      // s3/gcs/public), so we also route through the auth endpoint when
      // `has_image` is true — the streaming controller serves any disk.
      const authEndpoint =
        batchId != null && (page.image_url || page.has_image)
          ? API_ENDPOINTS.PHOTO_SCANNING.PAGE_IMAGE(batchId, page.id)
          : null;
      return {
        imageUrl: authEndpoint ? null : page.image_url,
        authEndpoint,
        label: page.page_number
          ? t('pageLabel', { number: page.page_number })
          : `#${page.id}`,
        chunks: visibleChunks,
        // Per page: during a selective re-run only the pages being re-read
        // spin; a full run in flight still marks every page.
        isProcessing: chunkSources.some((s) => isPageInFlight(s, index + 1))
      };
    });
  }, [
    containerPages,
    sortedResults,
    viewerResults,
    pool,
    templateFilter,
    t,
    highlightsEnabled,
    viewerOffsetFor,
    effectiveFieldColors,
    defaultHighlightColor
  ]);

  // ── Group fields by their owning page so each page gets its own section ──
  // Fields are split across pages by looking up each field's first chunk
  // reference in `chunks[].page_number`. A result with no extracted data yet
  // (a fresh run, a failure, a skipped duplicate) collapses to a single anchor
  // section so status indicators don't multiply across pages.
  //
  // The gate is `fields.length === 0`, NOT `status !== 'completed'`: a per-page
  // re-run flips the whole result to `processing` while every other page's
  // extraction is still stored and perfectly renderable. Gating on status
  // collapsed all of them into one section, which read as "the re-run wiped
  // everything". In-flight state is scoped per page via
  // `rerunning_page_numbers` instead.
  const pageGroups: PageGroup[] = useMemo(() => {
    const groups: Omit<PageGroup, 'isFirstGroupForResult'>[] = [];

    for (const r of panelResults) {
      const baseOffset = viewerOffsetFor(r);
      const pages = resolvedPages(r);
      const fields = r.extracted_data ?? [];

      if (fields.length === 0) {
        // Under a `fieldSources` filter an empty list means "this run had none
        // of the kind we are showing", not "this run extracted nothing" — so
        // there is nothing to anchor and a "no extracted data" section would
        // be a false report. The result stays in `sortedResults` either way,
        // so the viewer keeps its page images.
        if (fieldSources || fieldKeys) continue;

        // Covers no page any more — nothing to anchor, see `coversNoPage`.
        if (coversNoPage(r)) continue;

        // No extracted data to attribute, so the whole result collapses to one
        // anchor section and that anchor is the only thing naming the page the
        // run was about. `resolveEmptyResultAnchor` picks it from the run's own
        // assignment, falling back to `page_id` for rows predating the column —
        // see its docblock for why `page_id` alone always resolved to PG-001.
        // A sibling re-reading several pages for a new template has no
        // fields yet; give each of those pages its own loading section rather
        // than one anchor that hides the rest.
        const rerunPages = emptyResultSectionPages(r);
        if (rerunPages.length > 0) {
          for (const pn of rerunPages) {
            const idx = pn - 1;
            groups.push({
              result: r,
              page: pages[idx] ?? pages[0],
              pageIndexInResult: pn,
              viewerPageIndex: baseOffset + Math.min(idx, pages.length - 1),
              fields: [],
              isUnattributed: false
            });
          }
          continue;
        }

        const anchor = resolveEmptyResultAnchor({
          page_id: r.page_id,
          pages,
          assigned_page_numbers: r.assigned_page_numbers
        });
        groups.push({
          result: r,
          page: pages[anchor.pageIndex] ?? pages[0],
          pageIndexInResult: anchor.pageNumber,
          viewerPageIndex: baseOffset + anchor.pageIndex,
          fields,
          isUnattributed: false
        });
      } else {
        const chunkPage = new Map<string, number>();
        for (const c of r.chunks ?? [])
          chunkPage.set(c.chunk_id, c.page_number);

        // Same attribution the backend scores winners with — see
        // `bucketFieldsByPage`. Fields that resolve to no page get their own
        // section below instead of silently landing on page 1.
        const { byPage: fieldsByPage, unattributed } = bucketFieldsByPage(
          fields,
          chunkPage
        );

        // A page being re-run has had its fields stripped server-side (they are
        // stale until the re-extraction merges back), so it contributes no
        // fieldsByPage entry. Union it in explicitly or its section would
        // vanish the moment the operator clicks "Re-run this page" — the
        // opposite of the reassurance they need. It renders with an empty field
        // list, which PageFieldsSection shows as the extraction timeline.
        // Failed pages are unioned in for the same reason: they produced no
        // fields, and without a section the failure is invisible.
        const pageNums = sectionPageNumbers(fieldsByPage.keys(), r);
        for (const pn of pageNums) {
          const idx = pn - 1;
          const page = pages[idx] ?? pages[0];
          groups.push({
            result: r,
            page,
            pageIndexInResult: pn,
            viewerPageIndex: baseOffset + Math.min(idx, pages.length - 1),
            fields: fieldsByPage.get(pn) ?? [],
            isUnattributed: false
          });
        }

        // One extra section per result for the leftovers. It anchors to the
        // result's own page purely so the viewer has somewhere to point; the
        // sort below keeps it out of the page-ordered run.
        if (unattributed.length > 0) {
          const ownIndex = pages.findIndex((p) => p.id === r.page_id);
          groups.push({
            result: r,
            page: ownIndex >= 0 ? pages[ownIndex] : pages[0],
            pageIndexInResult: UNATTRIBUTED_PAGE_INDEX,
            viewerPageIndex: baseOffset + Math.max(0, ownIndex),
            fields: unattributed,
            isUnattributed: true
          });
        }
      }
    }

    // Page-major order: in the unified all-templates view this interleaves
    // sections as page 1 (template A, B, C), page 2 (A, B, C), … so the
    // panel reads in document order. Stable sort keeps sibling order within
    // a page. Unattributed sections sort after every page: they belong to no
    // page, so interleaving them by their anchor index would drop them into
    // the middle of the document.
    const ordered = groups.sort(
      (a, b) =>
        Number(a.isUnattributed) - Number(b.isUnattributed) ||
        a.viewerPageIndex - b.viewerPageIndex ||
        a.result.id - b.result.id
    );

    // Accepted/rejected pages filter — only narrows the list once the
    // container has a QA rejection (the toggle controlling this is hidden
    // otherwise, so a normal container's page list is never affected).
    // Tagged last, so the per-result anchor lands on the first group that
    // actually renders rather than on one the filter just dropped.
    if (containerQaStage !== 'qa_rejected' || pageAcceptanceFilter === 'all')
      return withFirstGroupPerResult(ordered);
    return withFirstGroupPerResult(
      ordered.filter(
        (g) =>
          pageQaBucket(g.result, g.pageIndexInResult) === pageAcceptanceFilter
      )
    );
  }, [
    panelResults,
    resolvedPages,
    viewerOffsetFor,
    containerQaStage,
    pageAcceptanceFilter,
    fieldSources,
    fieldKeys
  ]);

  // ── Cluster pageGroups that share the same physical page ──
  // A template-pool run produces one PageGroup per candidate template for
  // the SAME page (they share `template_pool_run_token` and resolve to the
  // same `viewerPageIndex`), which otherwise renders as duplicate-looking
  // full-width sections. Group those together so they render as one
  // "template pool" cluster instead. Order is preserved: `pageGroups` is
  // already page-major sorted, so siblings for one page are adjacent and
  // clusters come out in the same order the flat list would have rendered.
  const pageClusters = useMemo(() => {
    const withIndex: PageGroupWithIndex[] = pageGroups.map((group, i) => ({
      ...group,
      groupIndex: i
    }));
    const clusters: PageCluster[] = [];
    const clusterByKey = new Map<string, PageCluster>();
    for (const group of withIndex) {
      // Unattributed sections resolve to their result's anchor page, so a
      // shared key would fuse one per sibling into a fake "page 1 pool".
      // Key them by result instead — they are never siblings of anything.
      const key = group.isUnattributed
        ? `unattributed::${group.result.id}`
        : `${group.result.template_pool_run_token ?? group.result.id}::${group.viewerPageIndex}`;
      let cluster = clusterByKey.get(key);
      if (!cluster) {
        cluster = { key, entries: [] };
        clusterByKey.set(key, cluster);
        clusters.push(cluster);
      }
      cluster.entries.push(group);
    }
    return clusters;
  }, [pageGroups]);

  // ── Flatten fields for the search popover ──
  // Each hit carries its `groupIndex` (position in pageGroups) so a selected
  // result can re-open + scroll its panel section, plus the same page-label /
  // template-name attribution the rendered sections use.
  const searchableFields: ExtractedFieldHit[] = useMemo(() => {
    const items: ExtractedFieldHit[] = [];
    pageGroups.forEach((group, groupIndex) => {
      if (group.fields.length === 0) return;
      const pageLabel = group.isUnattributed
        ? t('unattributedFields')
        : group.page?.page_number
          ? t('pageLabel', { number: group.page.page_number })
          : `#${group.result.id}`;
      const templateName =
        pool != null &&
        templateFilter === 'all' &&
        group.result.template_pool_run_token != null
          ? (group.result.ocr_template?.name ??
            templateNameById.get(group.result.ocr_template_id ?? -1) ??
            null)
          : null;
      for (const field of group.fields) {
        items.push({
          result: group.result,
          field,
          groupIndex,
          pageLabel,
          templateName
        });
      }
    });
    return items;
  }, [pageGroups, t, pool, templateFilter, templateNameById]);

  // ── Chunk highlighting ──
  // Field lookup must use BOTH key and the first reference: the model provider emits the
  // same `key` per page when a "fixed" field appears on multiple pages, so a
  // pure-key find would always pick the first match (page 1) and ignore the
  // page the user is actually pointing at.
  const highlightedChunkIds = useMemo(() => {
    if (!highlightsEnabled || !hoveredField) return [];
    const result = panelResults.find((r) => r.id === hoveredField.resultId);
    return (
      result?.extracted_data?.find(
        (f) => f.key === hoveredField.key && fieldRef(f) === hoveredField.ref
      )?.references ?? []
    );
  }, [hoveredField, panelResults, highlightsEnabled]);

  const activeChunkIds = useMemo(() => {
    if (!highlightsEnabled || !activeField) return [];
    const result = panelResults.find((r) => r.id === activeField.resultId);
    return (
      result?.extracted_data?.find(
        (f) => f.key === activeField.key && fieldRef(f) === activeField.ref
      )?.references ?? []
    );
  }, [activeField, panelResults, highlightsEnabled]);

  // ── Stats ──
  // OMA-616 — the whole-page transcription renders as read-only text with no
  // highlight to check it against, so it is never accepted and never reaches
  // `processed`. Counting it here would peg `overallProgress` below 100 and
  // leave "Submit to QA" permanently disabled on every template that has the
  // built-in field switched on.
  const allFields = useMemo(
    () =>
      panelResults
        .flatMap((r) => r.extracted_data ?? [])
        .filter((f) => f.source !== 'full_page'),
    [panelResults]
  );
  const totalFieldCount = allFields.length;
  const verifiedFieldCount = allFields.filter(
    (f) => f.status === 'processed'
  ).length;
  const overallProgress =
    totalFieldCount > 0
      ? Math.round((verifiedFieldCount / totalFieldCount) * 100)
      : 0;
  // Counted in PHYSICAL pages, not result rows: duplicate-page accounting adds
  // one row per repeat, so "N of <rows>" reported a total that was neither the
  // container's page count nor anything an operator could recognise.
  const completedPageCount = useMemo(() => {
    const done = new Set<number>();
    for (const group of pageGroups) {
      if (group.result.status === 'completed') done.add(group.viewerPageIndex);
    }
    return done.size;
  }, [pageGroups]);
  const isAllVerified = totalFieldCount > 0 && overallProgress === 100;
  const qaStatus = useMemo(
    () => getQaStatusView(containerQaStage, t),
    [containerQaStage, t]
  );
  // `qa_rejected` does NOT count as "already submitted" — a rejected
  // container needs resubmission, so the submit footer must reappear for it
  // (the container stays `completed` and fully re-editable after a QA1
  // reject; see the companion backend fix).
  const hasBeenSubmittedToQa =
    containerQaStage != null && containerQaStage !== 'qa_rejected';

  // What this surface is allowed to do at all, mirroring the backend guard
  // (OcrResult::isOperatorEditable / isEditableAtQaTier). Everything below
  // reads from here instead of re-deriving it per control, which is how the
  // controls drifted apart from each other in the first place.
  const capabilities = resolvePanelCapabilities({
    surface,
    containerQaStage,
    isPreviewMode,
    qaTierActive
  });

  // ── Header (OMA-626 / OMA-627) ──
  // A SIBLING read to the results, not part of them: `containerResults` is a
  // bare `OcrResult[]` patched item by item by the websocket updater, and none
  // of the container's own identity lives on a result row. `templates: []` is a
  // normal answer (a container with no template), never an error.
  const containerSummaryQuery = useQuery(ocrQueries.container(containerId));
  const containerSummary = containerSummaryQuery.data?.data ?? null;

  // Whether a run is currently working this container. Re-running while one is
  // in flight would supersede it mid-extraction, so the header's actions read
  // as disabled rather than absent — an operator has to be able to see that
  // the control exists and why it is not available yet.
  const isRunInFlight = useMemo(
    () =>
      sortedResults.some(
        (r) =>
          r.status === 'pending' ||
          r.status === 'processing' ||
          r.status === 'paused'
      ),
    [sortedResults]
  );

  // OMA-624, half one: the nav sidebar still collapses on arrival so results
  // land front-and-centre, and the app header's own `SidebarTrigger` is how it
  // comes back — a second control for it on this screen was redundant, which
  // is why the header's toggle now governs the panel instead. Only the
  // operator surface borrows it, and never while previewing frozen history:
  // that is a read-only detour, not a work session.
  useResultsSidebar(surface === 'ocr' && !isPreviewMode);

  // OMA-624, half two: show or hide the extraction panel. The document is what
  // needs the room — a dense Arabic scan at a legible zoom does not fit beside
  // a 420px panel — and the preference is remembered.
  const { visible: panelVisible, toggle: togglePanel } = useResultsPanel();

  // Local state, not a hash key: this page already owns the fragment for its
  // own `#page/<n>` / `#field/...` deep links, and a hash overlay here would
  // overwrite the link an operator arrived on.
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  // ── Sync viewer to the page that holds the hovered field's chunks ──
  // Each result can span multiple viewer pages. The viewer page index is
  // `result_offset + (chunk.page_number - 1)` — using `findIndex(result.id)`
  // collapses every field onto the result's first page, which would scroll
  // the viewer back to page 1 every time.
  //
  // `ref` is the field's first chunk_id and is globally unique within the
  // result. The owning result may be a pool sibling that isn't in the viewer —
  // `viewerOffsetFor` maps it onto the anchor's pages. Null for a field with no
  // chunk: an ungrounded value has no region to bring into view.
  const viewerPageIndexOfField = useCallback(
    (target: FieldId | null): number | null => {
      if (!target || !target.ref) return null;
      const result = panelResults.find((r) => r.id === target.resultId);
      const chunk = (result?.chunks ?? []).find(
        (c) => c.chunk_id === target.ref
      );
      if (!result || !chunk) return null;
      return viewerOffsetFor(result) + Math.max(0, chunk.page_number - 1);
    },
    [panelResults, viewerOffsetFor]
  );

  const hoveredViewerPageIndex = useMemo(
    () => viewerPageIndexOfField(hoveredField),
    [viewerPageIndexOfField, hoveredField]
  );

  const activeFieldViewerPageIndex = useMemo(
    () => viewerPageIndexOfField(activeField),
    [viewerPageIndexOfField, activeField]
  );

  // Hover PARKS the viewer — it travels to the hovered field's page and stays
  // there. Falling straight back to `activePage` on pointer-leave made every
  // hover on an off-screen page bounce: the viewer's programmatic scroll is
  // guarded by `isAutoScrolling` so it never reports a page change, leaving
  // `activePage` still naming the page the hover came from. See
  // `utils/viewer-page.ts` for why the viewer can't just report its landings.
  useEffect(() => {
    setParkedViewerPage((current) =>
      nextParkedViewerPage(current, hoveredViewerPageIndex)
    );
  }, [hoveredViewerPageIndex]);

  // The user scrolling or paging the viewer is the only explicit command it
  // gets, and the only writer of `activePage` — so it is also what releases a
  // hover's park. Without the release, a park would outrank every later page
  // change and the viewer would refuse to move.
  const handleViewerPageChange = useCallback((pageIndex: number) => {
    setParkedViewerPage(null);
    setActivePage(pageIndex);
  }, []);

  // A field becoming ACTIVE is an explicit command — a card click, a search
  // hit, or a deep link — so unlike hover it moves the viewer for good and
  // takes the right panel with it. Without this the highlight was activated on
  // a page the viewer was not showing, so "jump to field" left the document
  // wherever it happened to be.
  useEffect(() => {
    if (activeFieldViewerPageIndex == null) return;
    handleViewerPageChange(activeFieldViewerPageIndex);
  }, [activeFieldViewerPageIndex, handleViewerPageChange]);

  const effectiveViewerPage = resolveViewerPage({
    hovered: hoveredViewerPageIndex,
    parked: parkedViewerPage,
    active: activePage
  });

  // ── Map state → right-panel group index ──
  // The right panel renders one section per PageGroup (per-page subset of a
  // result). The active viewer page and an explicit field click each map to
  // a group; we use that index for auto-scroll.
  //
  // Hover is intentionally NOT a trigger. Driving auto-scroll from hover
  // creates a feedback loop where mousing over fields while scrolling yanks
  // the panel between groups, making it feel uncontrollable.
  const activeGroupIndex = useMemo(() => {
    if (pageGroups.length === 0) return 0;
    // Unattributed sections are skipped throughout: they carry their result's
    // anchor index but sort last, so both the exact match and the
    // nearest-preceding scan (which assumes viewer-page order) would snap the
    // panel to the bottom of the list on every page turn.
    // Exact match by viewer page
    for (let i = 0; i < pageGroups.length; i++) {
      if (pageGroups[i].isUnattributed) continue;
      if (pageGroups[i].viewerPageIndex === activePage) return i;
    }
    // Else nearest preceding group (groups are emitted in viewer-page order)
    let best = 0;
    for (let i = 0; i < pageGroups.length; i++) {
      if (pageGroups[i].isUnattributed) continue;
      if (pageGroups[i].viewerPageIndex <= activePage) best = i;
    }
    return best;
  }, [activePage, pageGroups]);

  // Group containing the active field (set when the user clicks a chunk in
  // the viewer). Used to auto-open + scroll the right-panel section.
  const activeFieldGroupIndex = useMemo(() => {
    if (!activeField) return null;
    const idx = pageGroups.findIndex(
      (g) =>
        g.result.id === activeField.resultId &&
        g.fields.some(
          (f) => f.key === activeField.key && fieldRef(f) === activeField.ref
        )
    );
    return idx >= 0 ? idx : null;
  }, [activeField, pageGroups]);

  const effectiveGroupIndex =
    activeFieldGroupIndex != null ? activeFieldGroupIndex : activeGroupIndex;

  // ── Auto-scroll right panel to match the active group ──
  // Wait one frame so a freshly-opened collapsible has expanded before we
  // scroll — otherwise the smooth scroll lands at the collapsed height.
  // Skip if the user is actively scrolling the panel themselves.
  useEffect(() => {
    if (isUserScrollingPanel.current) return;
    const id = requestAnimationFrame(() => {
      if (isUserScrollingPanel.current) return;

      // A named field beats its section: land on the card itself, centred, so
      // the value is readable without a second scroll. Scrolling to the page
      // SECTION was as close as the panel could get, which on a 26-field page
      // leaves the target far below the fold — clicking a highlight in the
      // viewer looked like it did nothing for every field but the first few.
      // Falls back to the section when no field is active (we are only
      // following the viewer's page) or its card is not mounted yet.
      const card = activeField
        ? document.querySelector<HTMLElement>('[data-field-active="true"]')
        : null;

      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const ref = pageSectionRefs.current.get(effectiveGroupIndex);
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [effectiveGroupIndex, collapsedGroups, activeField]);

  // ── Collapse / expand all toggle ──
  const allGroupsCollapsed =
    pageGroups.length > 0 && collapsedGroups.size === pageGroups.length;

  const toggleAllGroups = useCallback(() => {
    setCollapsedGroups((prev) =>
      prev.size === pageGroups.length
        ? new Set()
        : new Set(pageGroups.map((_, i) => i))
    );
  }, [pageGroups]);

  const handleSetGroupOpen = useCallback(
    (groupIndex: number, open: boolean) => {
      setCollapsedGroups((prev) => {
        const next = new Set(prev);
        if (open) next.delete(groupIndex);
        else next.add(groupIndex);
        return next;
      });
    },
    []
  );

  // ── Handlers ──

  const handleAcceptField = useCallback(
    async (resultId: number, key: string, pageNumber?: number | null) => {
      // Defense in depth: the accept affordance is hidden via `readOnly` while
      // previewing history, but frozen results should never be mutated even
      // if this were reached some other way.
      if (isPreviewMode) return;
      const uid = fieldUid(resultId, key, pageNumber);
      markAccepting([uid], true);
      try {
        await acceptFieldMutation.mutateAsync({
          resultId,
          key,
          pageNumber,
          containerId
        });
      } catch {
        // handled by global MutationCache
      } finally {
        markAccepting([uid], false);
      }
    },
    [acceptFieldMutation, containerId, markAccepting, isPreviewMode]
  );

  const handleUpdateField = useCallback(
    async (
      resultId: number,
      key: string,
      value: string,
      pageNumber?: number | null
    ) => {
      if (isPreviewMode) return;
      // QA detail supplies an override so edits route through the QA-scoped
      // endpoint; it owns its own optimistic update + error handling.
      if (onUpdateFieldOverride) {
        await onUpdateFieldOverride(resultId, key, value, pageNumber);
        return;
      }
      try {
        await updateFieldMutation.mutateAsync({
          resultId,
          key,
          data: { value, page_number: pageNumber ?? undefined },
          containerId
        });
      } catch {
        // handled by global MutationCache
      }
    },
    [updateFieldMutation, containerId, onUpdateFieldOverride, isPreviewMode]
  );

  /**
   * Rename a field (OMA-631). Operator surface only — there is no QA-scoped
   * rename endpoint, and a reviewer decides about VALUES: what a field is
   * called is the operator's correction to make, on a container they still
   * own.
   *
   * Rethrows rather than swallowing, unlike the value edit beside it: the card
   * awaits this to decide whether to close its inline editor, and a rename the
   * server refused must not look like one that landed.
   */
  const handleRenameField = useCallback(
    async (
      resultId: number,
      key: string,
      name: string,
      pageNumber?: number | null
    ) => {
      if (isPreviewMode) return;

      await renameFieldMutation.mutateAsync({
        resultId,
        data: { key, name, page_number: pageNumber ?? undefined },
        containerId
      });
    },
    [renameFieldMutation, containerId, isPreviewMode]
  );

  const handleAcceptAllPage = useCallback(
    async (
      resultId: number,
      pageNumber: number,
      fields: Array<{ key: string; pageNumber?: number | null }>
    ) => {
      if (isPreviewMode) return;
      // Verify every field on this page. acceptAll only marks a result's fields
      // `processed` — it no longer submits to QA — so it's safe to use as a
      // one-request bulk verify when this page covers the whole result.
      // Submission is the separate, all-pages "Submit to QA" action below.
      const result = panelResults.find((r) => r.id === resultId);
      // Count-based comparison: `fields` is always a subset of the result's
      // unverified fields (the page group's slice), and keys are not unique
      // across pages for repeating templates — equal counts ⇒ this page group
      // covers every unverified field of the result.
      const allUnverifiedCount = (result?.extracted_data ?? []).filter(
        (f) => f.status !== 'processed' && f.source !== 'full_page'
      ).length;
      const acceptsWholeResult =
        fields.length > 0 && fields.length === allUnverifiedCount;

      const uids = fields.map((f) => fieldUid(resultId, f.key, f.pageNumber));
      markAccepting(uids, true);
      try {
        if (acceptsWholeResult) {
          try {
            await acceptAllMutation.mutateAsync({ resultId, containerId });
          } catch {
            // handled by global MutationCache
          }
        } else {
          // Page is a subset of the result — one page-scoped bulk request.
          // This used to loop one acceptField call per field, which was slow
          // enough on a hundred-field page that "Submit to QA" could be
          // pressed while accepts were still in flight, and any swallowed
          // per-field failure left invisible pending fields blocking it.
          try {
            await acceptAllOnPageMutation.mutateAsync({
              resultId,
              pageNumber,
              containerId
            });
          } catch {
            // handled by global MutationCache
          }
        }
      } finally {
        markAccepting(uids, false);
      }
    },
    [
      panelResults,
      acceptAllMutation,
      acceptAllOnPageMutation,
      containerId,
      markAccepting,
      isPreviewMode
    ]
  );

  const handleSubmitAll = useCallback(async () => {
    if (isPreviewMode) return;
    if (!isAllVerified) {
      toast.error(t('submitBlockedIncomplete'));
      return;
    }
    try {
      // The single, deliberate submission step: one call stamps qa_stage on
      // every page and advances the registration ocr → qa1_ocr. The backend
      // re-checks that every field is accepted (422 otherwise), so the whole
      // container moves to QA atomically — never off a single page.
      await submitToQaMutation.mutateAsync({ containerId });
      toast.success(t('allPagesCompleted'));
      router.push('/dashboard/ocr-containers');
    } catch {
      // handled by global MutationCache
    }
  }, [
    isAllVerified,
    submitToQaMutation,
    t,
    router,
    containerId,
    isPreviewMode
  ]);

  // Re-run OCR after a QA rejection re-processes the whole container, not
  // just the page(s) that got rejected — a partial re-run would leave the
  // container's pages on mismatched extraction runs.
  const handleRerunOcr = useCallback(async () => {
    if (isPreviewMode) return;
    try {
      await rerunOcrMutation.mutateAsync({ containerId });
    } catch {
      // handled by global MutationCache
    }
  }, [rerunOcrMutation, containerId, isPreviewMode]);

  // Re-extract ONE page instead of the whole container. The backend keeps every
  // other page's extraction, chunks and QA history, so a container bounced for
  // a single bad page doesn't pay for a full re-run.
  const handleRerunPage = useCallback(
    (resultId: number, pageNumber: number) => {
      if (isPreviewMode) return;
      rerunPageMutation
        .mutateAsync({ resultId, pageNumber, containerId })
        .catch(() => {
          // handled by global MutationCache
        });
    },
    [rerunPageMutation, containerId, isPreviewMode]
  );

  // Offered only on a container QA sent back, and only to a user who could
  // start OCR in the first place. PermissionGate can't express this because the
  // affordance is passed down as a prop, not rendered here.
  const canRerunPages =
    capabilities.operatorToolsVisible &&
    containerQaStage === 'qa_rejected' &&
    user != null &&
    (isSuperAdmin(user) || hasAnyPermission(user, ['ocr.edit']));

  // Accepting a field (or a whole page) is not a one-way door: an operator who
  // spots a typo right after approving it keeps the pencil for as long as the
  // container is the OCR stage's — before submission, and again after a QA1
  // reject, which keeps every page `completed`/`processed`
  // (OcrQaService::bounceBack) and would otherwise leave nothing to do but
  // re-submit the exact values QA just rejected. Backend gate is `ocr.edit`
  // alone (`PUT /ocr/results/{id}/field` has no QA-stage guard).
  const canEditOcrFields = canReopenVerifiedFields({
    isPreviewMode,
    hasBeenSubmittedToQa,
    canEditOcr:
      user != null &&
      (isSuperAdmin(user) || hasAnyPermission(user, ['ocr.edit']))
  });

  const handleChunkClick = useCallback(
    (chunkId: string) => {
      for (const result of panelResults) {
        const field = result.extracted_data?.find((f) =>
          f.references.includes(chunkId)
        );
        if (field) {
          const ref = fieldRef(field);
          setActiveField({ resultId: result.id, key: field.key, ref });
          // Auto-open the right-panel section that owns this field so the
          // chunk-click reveals the matching extracted value (the scroll
          // effect picks it up via `effectiveGroupIndex`).
          const groupIdx = pageGroups.findIndex(
            (g) =>
              g.result.id === result.id &&
              g.fields.some((f) => f.key === field.key && fieldRef(f) === ref)
          );
          if (groupIdx >= 0) handleSetGroupOpen(groupIdx, true);
          return;
        }
      }
    },
    [panelResults, pageGroups, handleSetGroupOpen]
  );

  const handleChunkHover = useCallback(
    (chunkId: string | null) => {
      if (!chunkId) {
        setHoveredField(null);
        return;
      }
      for (const result of panelResults) {
        const field = result.extracted_data?.find((f) =>
          f.references.includes(chunkId)
        );
        if (field) {
          setHoveredField({
            resultId: result.id,
            key: field.key,
            ref: fieldRef(field)
          });
          return;
        }
      }
    },
    [panelResults]
  );

  // Persist a manual drag-resize/move of a chunk's bounding box. Finds the
  // owning result the same way handleChunkClick/handleChunkHover do (loop +
  // find by chunk_id), since the viewer only knows the chunk's id, not which
  // result it belongs to.
  // OMA-593: a move/resize is a DRAFT. It is recorded here and rendered back
  // through the viewer's `pendingBboxes`, and only reaches the server when the
  // operator presses Save — a drag is easy to do by accident while panning, and
  // silently persisting one rewrote where the value was read from.
  const handleChunkBboxChange = useCallback(
    (chunkId: string, bbox: UpdateChunkBboxRequest) => {
      if (isPreviewMode) return;
      applyHighlightDraft((draft) => moveHighlight(draft, chunkId, bbox));
    },
    [applyHighlightDraft, isPreviewMode]
  );

  // OMA-592: arm draw mode for a field. Every page then accepts a drag, and the
  // mode stays on so a value spanning several regions can be boxed in one pass.
  const handleAddHighlight = useCallback(
    (resultId: number, fieldKey: string, pageNumber?: number | null) => {
      if (isPreviewMode) return;
      setDrawTarget((prev) =>
        prev?.resultId === resultId && prev.fieldKey === fieldKey
          ? null
          : { resultId, fieldKey, fieldPageNumber: pageNumber ?? null }
      );
    },
    [isPreviewMode]
  );

  const handleDrawHighlight = useCallback(
    (pageIndex: number, bbox: UpdateChunkBboxRequest) => {
      if (!drawTarget) return;
      applyHighlightDraft((draft) =>
        addHighlight(draft, {
          tempId: `draft-${pageIndex}-${bbox.left}-${bbox.top}-${draft.additions.length}`,
          resultId: drawTarget.resultId,
          fieldKey: drawTarget.fieldKey,
          fieldPageNumber: drawTarget.fieldPageNumber,
          pageIndex,
          bbox
        })
      );
    },
    [applyHighlightDraft, drawTarget]
  );

  // ── Add a field by hand (OMA-630) ──

  /**
   * The result whose pages the viewer index falls in.
   *
   * A container's results each cover a slice of the viewer's flat page list, so
   * the new field has to be created on the one that owns the page the box was
   * drawn on — filing it elsewhere stores a `page_number` that result does not
   * have and the viewer never draws the highlight back. Derived from the same
   * `viewerOffsetFor` the highlight save uses, rather than from `pageGroups`:
   * a page that produced no fields has no group, and that is exactly the page
   * an operator is most likely to be adding a missing field to.
   */
  const resultOwningViewerPage = useCallback(
    (pageIndex: number): OcrResult | null => {
      let owner: OcrResult | null = null;
      let ownerOffset = -1;

      for (const result of panelResults) {
        const offset = viewerOffsetFor(result);
        if (offset <= pageIndex && offset > ownerOffset) {
          owner = result;
          ownerOffset = offset;
        }
      }

      return owner ?? panelResults[0] ?? null;
    },
    [panelResults, viewerOffsetFor]
  );

  const handleArmManualFieldDraw = useCallback((draft: ManualFieldDraft) => {
    setPendingManualField(draft);
    setAddFieldOpen(false);
    // Arming both at once would make one drag mean two things.
    setDrawTarget(null);
  }, []);

  const handleOpenAddFieldForPage = useCallback(
    (resultId: number, pageNumber: number) => {
      setAddFieldTarget({ resultId, pageNumber });
      setAddFieldOpen(true);
    },
    []
  );

  /**
   * Save a field straight from a page section, with no region (OMA-630
   * follow-up).
   *
   * The whole draw step is skipped, so `bbox` is OMITTED from the payload
   * rather than sent empty — the server reads its absence as "ungrounded" and
   * stores the field with no `references`, which is the same shape a model
   * extraction with no geometry carries and the same thing the card badges.
   *
   * `page_number` is used verbatim: a section's `pageIndexInResult` is already
   * 1-based within the owning result, which is the space the endpoint stores.
   * The draw flow has to map a viewer index onto that; this one does not.
   */
  const handleSaveManualField = useCallback(
    async (
      draft: ManualFieldDraft,
      target: { resultId: number; pageNumber: number }
    ) => {
      try {
        const response = await createOcrFieldMutation.mutateAsync({
          resultId: target.resultId,
          containerId,
          data: {
            name: draft.name,
            value: draft.value,
            page_number: target.pageNumber
          }
        });

        // Against the key the SERVER minted, for the same reason the draw flow
        // does: a collision suffix means it can differ from the typed name's
        // fold, and a colour stored under the wrong key tints nothing.
        setFieldColor(response.data.field_key, draft.color);
        setAddFieldOpen(false);
      } catch {
        // Raised by the global MutationCache. The dialog stays open with the
        // typed values intact, so a 422 is corrected rather than retyped.
      }
    },
    [createOcrFieldMutation, containerId, setFieldColor]
  );

  const handleDrawManualField = useCallback(
    async (pageIndex: number, bbox: UpdateChunkBboxRequest) => {
      const draft = pendingManualField;
      if (!draft) return;

      // AC3 — a zero-sized or off-page drag is refused here with a message
      // rather than sent for the backend to 422, so the operator is told while
      // the page is still under their cursor. Stays armed: the fix is another
      // drag, not retyping the field.
      if (validateManualFieldDraft(draft, bbox).bbox) {
        toast.error(t('addFieldRegionInvalid'));
        return;
      }

      const owner = resultOwningViewerPage(pageIndex);
      if (!owner) return;

      try {
        const response = await createOcrFieldMutation.mutateAsync({
          resultId: owner.id,
          containerId,
          data: {
            name: draft.name,
            value: draft.value,
            page_number: resultLocalPageNumber(
              pageIndex,
              viewerOffsetFor(owner)
            ),
            bbox
          }
        });

        // Against the key the SERVER minted, not the one folded from the typed
        // name: a collision suffix means they can differ, and a colour stored
        // under the wrong key tints nothing.
        setFieldColor(response.data.field_key, draft.color);
        setPendingManualField(null);
      } catch {
        // Raised by the global MutationCache. The draft stays armed so the
        // operator can redraw instead of retyping the whole field.
      }
    },
    [
      pendingManualField,
      resultOwningViewerPage,
      createOcrFieldMutation,
      containerId,
      viewerOffsetFor,
      setFieldColor,
      t
    ]
  );

  // One handler for both kinds of box: a drawn-but-unsaved one is dropped from
  // the draft, a persisted one is queued for unlinking.
  const handleRemoveHighlight = useCallback(
    (chunkIdOrTempId: string) => {
      if (isPreviewMode) return;

      applyHighlightDraft((draft) => {
        if (draft.additions.some((a) => a.tempId === chunkIdOrTempId)) {
          return discardAddition(draft, chunkIdOrTempId);
        }

        for (const result of panelResults) {
          const chunk = result.chunks?.find(
            (c) => c.chunk_id === chunkIdOrTempId
          );
          if (!chunk) continue;

          const field = result.extracted_data?.find((f) =>
            f.references.includes(chunkIdOrTempId)
          );
          if (!field) continue;

          return removeHighlight(draft, {
            resultId: result.id,
            fieldKey: field.key,
            fieldPageNumber: field.page_number ?? null,
            chunkDbId: chunk.id,
            chunkId: chunk.chunk_id
          });
        }

        return draft;
      });
    },
    [applyHighlightDraft, panelResults, isPreviewMode]
  );

  const discardHighlightEdits = useCallback(() => {
    resetHighlightDraft();
    setDrawTarget(null);
  }, [resetHighlightDraft]);

  const saveHighlightEdits = useCallback(async () => {
    const resultById = new Map(panelResults.map((r) => [r.id, r]));
    // The chunk a move targets is resolved once here rather than stored on the
    // draft, so the draft stays a plain value the history can snapshot.
    const moveTargets = Object.entries(highlightDraft.moves).flatMap(
      ([chunkId, bbox]) => {
        for (const result of panelResults) {
          const chunk = result.chunks?.find((c) => c.chunk_id === chunkId);
          if (chunk)
            return [{ resultId: result.id, chunkDbId: chunk.id, bbox }];
        }
        return [];
      }
    );

    try {
      for (const move of moveTargets) {
        await updateChunkBboxMutation.mutateAsync({
          resultId: move.resultId,
          chunkId: move.chunkDbId,
          data: move.bbox,
          containerId
        });
      }

      for (const removal of highlightDraft.removals) {
        await deleteFieldHighlightMutation.mutateAsync({
          resultId: removal.resultId,
          chunkId: removal.chunkDbId,
          containerId,
          data: {
            key: removal.fieldKey,
            page_number: removal.fieldPageNumber ?? undefined
          }
        });
      }

      for (const addition of highlightDraft.additions) {
        const owner = resultById.get(addition.resultId);
        await createFieldHighlightMutation.mutateAsync({
          resultId: addition.resultId,
          containerId,
          data: {
            key: addition.fieldKey,
            // The viewer counts pages across the whole container; a chunk's
            // page_number is 1-based within its OWN result's page list.
            page_number: resultLocalPageNumber(
              addition.pageIndex,
              owner ? viewerOffsetFor(owner) : 0
            ),
            bbox: addition.bbox
          }
        });
      }

      // Colors are client-side, so they go last: only once the server-side
      // edits have all landed does the draft get cleared, and a failure above
      // leaves the recolor pending too rather than half-applying the batch.
      for (const [fieldKey, color] of Object.entries(highlightDraft.colors)) {
        setFieldColor(fieldKey, color);
      }

      discardHighlightEdits();
    } catch {
      // Handled by the global MutationCache; the draft stays put so the
      // operator can retry instead of losing the adjustment.
    }
  }, [
    highlightDraft,
    panelResults,
    viewerOffsetFor,
    updateChunkBboxMutation,
    createFieldHighlightMutation,
    deleteFieldHighlightMutation,
    containerId,
    setFieldColor,
    discardHighlightEdits
  ]);

  // Shape the draft the way the viewer consumes it.
  const pendingViewerBboxes = useMemo(
    () => new Map(Object.entries(highlightDraft.moves)),
    [highlightDraft.moves]
  );
  const hiddenChunkIds = useMemo(
    () => removedChunkIds(highlightDraft),
    [highlightDraft]
  );
  const unsavedViewerHighlights = useMemo(
    () =>
      highlightDraft.additions.map((a) => ({
        tempId: a.tempId,
        pageIndex: a.pageIndex,
        bbox: a.bbox
      })),
    [highlightDraft.additions]
  );
  const pendingHighlightCount = highlightDraftSize(highlightDraft);

  const isSavingHighlights =
    updateChunkBboxMutation.isPending ||
    createFieldHighlightMutation.isPending ||
    deleteFieldHighlightMutation.isPending;

  // Covers a reload / tab close. An in-app route change cannot be intercepted
  // in the App Router, so the persistent bar below is what warns there.
  useEffect(() => {
    if (!hasUnsavedHighlights) return;

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedHighlights]);

  // A recolor is a DRAFT, like a move or a drawn box: it previews immediately
  // but only reaches the stored colors when the operator presses Save, and
  // Cancel/undo puts the old color back. Still a per-document, client-side
  // override once saved — the template's field color is never touched.
  const handleFieldColorChange = useCallback(
    (fieldKey: string, color: string | null) => {
      if (isPreviewMode) return;
      applyHighlightDraft((draft) => recolorField(draft, fieldKey, color));
    },
    [applyHighlightDraft, isPreviewMode]
  );

  // Recolor a field straight from its highlight box in the viewer (right-click
  // menu). The viewer only knows chunk ids, so resolve the owning field's key
  // the same way the hover/click handlers resolve the field itself.
  const handleChunkColorChange = useCallback(
    (chunkId: string, color: string | null) => {
      if (isPreviewMode) return;
      for (const result of panelResults) {
        const field = result.extracted_data?.find((f) =>
          f.references.includes(chunkId)
        );
        if (field) {
          handleFieldColorChange(field.key, color);
          return;
        }
      }
    },
    [panelResults, handleFieldColorChange, isPreviewMode]
  );

  // Chunks whose color comes from an override — drives the "reset to template
  // color" action inside the viewer's right-click menu.
  const overriddenColorChunkIds = useMemo(() => {
    const ids = new Set<string>();
    for (const result of panelResults) {
      for (const field of result.extracted_data ?? []) {
        if (effectiveFieldColors[field.key] == null) continue;
        for (const ref of field.references) ids.add(ref);
      }
    }
    return ids;
  }, [panelResults, effectiveFieldColors]);

  // Jump to a field picked from the search popover: open its panel section
  // and mark it active. Marking it active reuses the existing scroll + viewer
  // sync (via `activeFieldGroupIndex` / `activeChunkIds`), so the document
  // viewer follows along to the matching page.
  const handleJumpToField = useCallback(
    (hit: ExtractedFieldHit) => {
      handleSetGroupOpen(hit.groupIndex, true);
      setActiveField({
        resultId: hit.result.id,
        key: hit.field.key,
        ref: fieldRef(hit.field)
      });
    },
    [handleSetGroupOpen]
  );

  // ── Deep links from the container-results sheet ──────────────────────────
  // `#page/<n>` shows that page; `#field/<resultId>/<key>` opens the field's
  // section and activates it, which (via the effect above) brings the viewer to
  // its highlight. Applied once per TARGET: the results arrive after the target
  // does, and re-applying on every render would fight the operator's own
  // scrolling for the rest of the session.
  //
  // Keyed on the target's content rather than its identity, because the `deepLink`
  // prop is parsed fresh by the host on every render — an identity check would
  // re-apply the same jump forever and pin the panel to that field.
  const appliedDeepLink = useRef<string | null>(null);

  useEffect(() => {
    if (pageGroups.length === 0) return;

    // The prop wins: a host that owns the fragment (the OCR screen's results
    // overlay) has already parsed it, and the raw hash there names the overlay,
    // not a target.
    const target = deepLink ?? parseResultsDeepLink(hash);
    if (!target) return;

    const targetKey = JSON.stringify(target);
    if (appliedDeepLink.current === targetKey) return;
    appliedDeepLink.current = targetKey;

    if (target.kind === 'page') {
      // The fragment carries a 1-based GLOBAL page number; the viewer indexes
      // its flat page array from 0. Clamped, so a stale link to a page the
      // container no longer has lands on the last one instead of nowhere.
      handleViewerPageChange(
        Math.min(
          Math.max(target.pageNumber - 1, 0),
          Math.max(viewerPages.length - 1, 0)
        )
      );
      return;
    }

    // Page-aware, because the key alone names one field per page: a repeating
    // template extracts `تاريخ_الفاتورة` on all three pages of an invoice, and
    // matching on the key first hit page 1's copy for every link. The exact
    // match is tried first; a key-only match is the fallback, so a link written
    // before a re-run re-bucketed the field still lands on it rather than
    // nowhere.
    const onTargetPage = (candidate: ExtractedFieldHit): boolean => {
      const group = pageGroups[candidate.groupIndex];
      if (!group) return false;
      const page =
        candidate.field.page_number ??
        (group.isUnattributed ? 0 : group.viewerPageIndex + 1);
      return page === target.pageNumber;
    };

    const matches = searchableFields.filter(
      (candidate) =>
        candidate.result.id === target.resultId &&
        candidate.field.key === target.fieldKey
    );
    const hit = matches.find(onTargetPage) ?? matches[0];
    if (hit) handleJumpToField(hit);
  }, [
    hash,
    deepLink,
    pageGroups,
    viewerPages.length,
    searchableFields,
    handleJumpToField,
    handleViewerPageChange
  ]);

  // ── Loading ──
  if (isPending) {
    return (
      <div className='flex h-[calc(100svh-7rem)] min-h-[320px] items-center justify-center sm:h-[85svh]'>
        <Loader2 className='text-muted-foreground h-8 w-8 animate-spin' />
      </div>
    );
  }

  // ── No results ──
  if (sortedResults.length === 0) {
    return (
      <div className='flex h-[calc(100svh-7rem)] min-h-[320px] flex-col items-center justify-center gap-3 sm:h-[85svh]'>
        <ScanText className='text-muted-foreground/30 h-12 w-12' />
        <p className='text-muted-foreground'>{t('noResults')}</p>
      </div>
    );
  }

  // ── Main layout ──
  const QaIcon = qaStatus.icon;

  // Renders a single page section — identical output to what the flat
  // `pageGroups.map` used to render. Shared by both the size-1 (no visual
  // change) and clustered (nested inside a template-pool wrapper) cases
  // below. `labelOverride` lets the cluster case swap the page number for
  // the template name, since the cluster header already shows the page.
  const renderPageGroupEntry = (
    group: PageGroupWithIndex,
    labelOverride?: string
  ) => {
    const groupIndex = group.groupIndex;
    const pageLabel = group.isUnattributed
      ? t('unattributedFields')
      : (labelOverride ??
        (group.page?.page_number
          ? t('pageLabel', { number: group.page.page_number })
          : `#${group.result.id}`));
    // Per-page winner hint: chunk page numbers are global within a
    // pool run, so pageIndexInResult keys straight into the map. An
    // unattributed section won no page and can't be compared against one.
    const pageWinnerTemplateId =
      pool != null &&
      !group.isUnattributed &&
      group.result.template_pool_run_token != null &&
      poolWinnerMap != null
        ? (poolWinnerMap[String(group.pageIndexInResult)] ?? null)
        : null;
    return (
      <div
        key={`${group.result.id}:${group.pageIndexInResult}`}
        ref={(el) => {
          if (el) pageSectionRefs.current.set(groupIndex, el);
          else pageSectionRefs.current.delete(groupIndex);
        }}
      >
        {pageWinnerTemplateId != null &&
          (pageWinnerTemplateId === group.result.ocr_template_id ? (
            <div className='px-5 pt-3'>
              <Badge
                variant='outline'
                className='border-emerald-500/30 bg-emerald-500/10 text-[10px] text-emerald-600'
              >
                <Trophy className='me-1 h-3 w-3' />
                {t('bestMatch')}
              </Badge>
            </div>
          ) : templateFilter !== 'all' ? (
            // When filtered to one template the winner's section is
            // hidden — offer the jump. In the unified view the
            // winning section sits right next to this one, so the
            // hint would be noise.
            <div className='px-5 pt-3'>
              <button
                type='button'
                onClick={() => {
                  const target = pool?.find(
                    (r) => r.ocr_template_id === pageWinnerTemplateId
                  );
                  if (target) setTemplateFilter(target.id);
                }}
                className='flex items-center gap-1.5 text-xs text-amber-600 hover:underline'
              >
                <Trophy className='h-3 w-3' />
                {t('betterMatchHint', {
                  template:
                    templateNameById.get(pageWinnerTemplateId) ??
                    `#${pageWinnerTemplateId}`
                })}
              </button>
            </div>
          ) : null)}
        {group.isUnattributed && (
          <div className='px-5 pt-3'>
            <p className='flex items-start gap-1.5 text-xs text-amber-600'>
              <TriangleAlert className='mt-0.5 h-3 w-3 shrink-0' />
              <span>{t('unattributedFieldsHint')}</span>
            </p>
          </div>
        )}
        <PageFieldsSection
          result={group.result}
          fields={group.fields}
          pageLabel={pageLabel}
          pageNumber={group.pageIndexInResult}
          templateName={
            pool != null &&
            templateFilter === 'all' &&
            group.result.template_pool_run_token != null
              ? (group.result.ocr_template?.name ??
                templateNameById.get(group.result.ocr_template_id ?? -1) ??
                null)
              : null
          }
          open={!collapsedGroups.has(groupIndex)}
          onOpenChange={(open) => handleSetGroupOpen(groupIndex, open)}
          activeField={highlightsEnabled ? activeField : null}
          hoveredField={highlightsEnabled ? hoveredField : null}
          highlightsEnabled={highlightsEnabled}
          onFieldHover={setHoveredField}
          onFieldClick={setActiveField}
          onAcceptField={handleAcceptField}
          onUpdateField={handleUpdateField}
          onRenameField={
            capabilities.operatorToolsVisible && !isPreviewMode
              ? handleRenameField
              : undefined
          }
          onAcceptAllPage={handleAcceptAllPage}
          // Single-page re-extraction is offered only on a container QA sent
          // back — that's the flow where one bad page shouldn't cost a full
          // container re-run. Gated on ocr.edit like the container-level button.
          // Never on the unattributed section: it addresses no page to re-run.
          onRerunPage={
            canRerunPages && !group.isUnattributed ? handleRerunPage : undefined
          }
          // Same gate as the toolbar's add-field button — never a QA reviewer,
          // never a frozen history snapshot (`operatorToolsVisible` folds
          // `isPreviewMode` in). Never the unattributed section either: it
          // addresses no page, so it has none to file a new field against.
          onAddField={
            capabilities.operatorToolsVisible && !group.isUnattributed
              ? handleOpenAddFieldForPage
              : undefined
          }
          addFieldDisabled={isRunInFlight || pendingManualField != null}
          acceptingFieldIds={acceptingFieldIds}
          // Drawing a highlight writes a chunk, so it rides the same gate as
          // dragging one — `ocr.edit`, never a frozen history snapshot.
          onAddHighlight={
            enableBboxEditing && capabilities.operatorToolsVisible
              ? handleAddHighlight
              : undefined
          }
          drawingFieldKey={drawTarget?.fieldKey ?? null}
          allowEditVerified={allowEditVerified || canEditOcrFields}
          readOnly={!capabilities.fieldsEditable}
          operatorTools={capabilities.operatorToolsVisible}
          fieldColorOverrides={effectiveFieldColors}
          defaultHighlightColor={defaultHighlightColor}
          // Recolouring is an operator tool: it writes a per-document
          // override, which is not a reviewer's call and is meaningless on a
          // frozen history snapshot.
          onFieldColorChange={
            capabilities.operatorToolsVisible
              ? handleFieldColorChange
              : undefined
          }
          renderFooter={
            renderPageFooter
              ? () =>
                  renderPageFooter(
                    group.result,
                    group.pageIndexInResult,
                    group.isFirstGroupForResult
                  )
              : undefined
          }
          renderFieldAction={
            renderFieldAction
              ? (field) => renderFieldAction(group.result, field)
              : undefined
          }
        />
      </div>
    );
  };

  // ── Extraction panel (rendered inline on md+ and inside a Sheet on mobile) ──
  const extractionPanel = (
    <>
      {/* Header */}
      <div className='border-b px-4 py-3 sm:px-5 sm:py-4'>
        <div className='flex items-center justify-between gap-2'>
          <div className='min-w-0 flex-1'>
            <h3 className='truncate text-sm font-semibold'>
              {t('extractedData')}
            </h3>
            <p className='text-muted-foreground truncate text-xs'>
              {t('containerProgress', {
                completed: completedPageCount,
                total: containerPages.length
              })}
            </p>
          </div>
          <div className='flex shrink-0 items-center gap-1.5'>
            {searchableFields.length > 0 && (
              <ExtractedDataSearch
                fields={searchableFields}
                onJump={handleJumpToField}
              />
            )}
            <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
              <PopoverTrigger asChild>
                <Button
                  size='sm'
                  variant='ghost'
                  className='h-7 gap-1.5 px-2 text-xs'
                >
                  <History className='h-3.5 w-3.5' />
                  {t('historyTitle')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-[360px] p-3' align='end'>
                <SuggestionHistoryPanel
                  containerId={containerId}
                  mode='readonly'
                  enabled={historyOpen}
                  onPreview={setPreviewRun}
                />
              </PopoverContent>
            </Popover>
            {pageGroups.length > 1 && (
              <Button
                size='sm'
                variant='ghost'
                className='h-7 px-2 text-xs'
                onClick={toggleAllGroups}
                aria-label={
                  allGroupsCollapsed ? t('expandAll') : t('collapseAll')
                }
                title={allGroupsCollapsed ? t('expandAll') : t('collapseAll')}
              >
                {allGroupsCollapsed ? (
                  <ChevronsUpDown className='h-3.5 w-3.5' />
                ) : (
                  <ChevronsDownUp className='h-3.5 w-3.5' />
                )}
              </Button>
            )}
            {viewerPages.length > 1 && (
              <Badge variant='outline' className='text-xs whitespace-nowrap'>
                {t('pageOf', {
                  current: effectiveViewerPage + 1,
                  total: viewerPages.length
                })}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Template filter — multi-template pool runs only. Defaults to a
          unified view of every template's extraction; picking a template
          narrows the panel to that template's data. */}
      {pool && (
        <div className='flex items-center gap-2 border-b px-3 py-2'>
          <span className='text-muted-foreground shrink-0 text-xs font-medium'>
            {t('filterByTemplate')}
          </span>
          <Select
            value={templateFilter === 'all' ? 'all' : String(templateFilter)}
            onValueChange={(v) =>
              setTemplateFilter(v === 'all' ? 'all' : Number(v))
            }
          >
            <SelectTrigger size='sm' className='h-8 min-w-0 flex-1 text-xs'>
              <SelectValue placeholder={t('allTemplates')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all' className='text-xs'>
                {t('allTemplates')}
              </SelectItem>
              {pool.map((r) => {
                const winCount =
                  poolWinCounts.get(r.ocr_template_id ?? -1) ?? 0;
                const unattributedCount = poolUnattributedCounts.get(r.id) ?? 0;
                const isTopWinner =
                  r.ocr_template_id != null &&
                  r.ocr_template_id === topWinnerTemplateId;
                return (
                  <SelectItem
                    key={r.id}
                    value={String(r.id)}
                    textValue={r.ocr_template?.name ?? `#${r.id}`}
                    className='text-xs'
                  >
                    {isTopWinner && (
                      <Trophy className='size-3.5 shrink-0 text-amber-500' />
                    )}
                    <span className='truncate'>
                      {r.ocr_template?.name ?? `#${r.id}`}
                    </span>
                    {(winCount > 0 || unattributedCount > 0) && (
                      <span className='ms-auto flex shrink-0 items-center gap-1'>
                        {winCount > 0 && (
                          <Badge
                            variant='secondary'
                            className='h-4 min-w-4 px-1 text-[10px]'
                            title={t('bestMatchPagesCount', {
                              count: winCount
                            })}
                          >
                            {winCount}
                          </Badge>
                        )}
                        {unattributedCount > 0 && (
                          <Badge
                            variant='outline'
                            className='h-4 min-w-4 gap-0.5 border-amber-500/30 bg-amber-500/10 px-1 text-[10px] text-amber-600'
                            title={t('unattributedFieldsCount', {
                              count: unattributedCount
                            })}
                          >
                            <TriangleAlert className='h-2.5 w-2.5' />
                            {unattributedCount}
                          </Badge>
                        )}
                      </span>
                    )}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Accepted/rejected pages filter — only shown once the container
          has a QA rejection, so the operator can focus on just the pages
          that need fixing vs. the ones already accepted. Independent of
          the template filter above. */}
      {containerQaStage === 'qa_rejected' && (
        <div className='flex flex-col items-start gap-1.5 border-b px-3 py-2'>
          <span className='text-muted-foreground text-xs font-medium'>
            {t('filterByPageStatus')}
          </span>
          <ToggleGroup
            className='flex-wrap justify-start'
            type='single'
            size='sm'
            variant='outline'
            value={pageAcceptanceFilter}
            onValueChange={(v) => {
              if (v)
                setPageAcceptanceFilter(
                  v as 'all' | 'accepted' | 'rejected' | 'pending'
                );
            }}
          >
            <ToggleGroupItem value='all' className='text-xs'>
              {t('allPages')}
            </ToggleGroupItem>
            <ToggleGroupItem value='accepted' className='text-xs'>
              {t('acceptedPages')}
            </ToggleGroupItem>
            <ToggleGroupItem value='rejected' className='text-xs'>
              {t('rejectedPages')}
            </ToggleGroupItem>
            <ToggleGroupItem value='pending' className='text-xs'>
              {t('notReviewedPages')}
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      )}

      {/* One aggregated progress bar for the whole multi-template run */}
      {pool && poolProgress && (
        <div className='border-b px-5 py-2.5'>
          <div className='flex items-center justify-between text-xs'>
            <span className='text-muted-foreground flex items-center gap-1.5'>
              <Loader2 className='h-3 w-3 animate-spin' />
              {poolProgress.rerunPages.length > 0
                ? t('poolRerunning', {
                    count: poolProgress.rerunPages.length,
                    pages: poolProgress.rerunPages
                      .map(
                        (n) =>
                          containerPages[n - 1]?.page.page_number ?? `#${n}`
                      )
                      .join(', ')
                  })
                : t('poolProcessing', { count: poolProgress.inFlightCount })}
            </span>
            <span className='font-medium'>{poolProgress.percent}%</span>
          </div>
          <Progress value={poolProgress.percent} className='mt-1.5 h-1.5' />
        </div>
      )}

      {/* Page sections */}
      <div
        className='flex-1 overflow-y-auto'
        onWheel={markUserScrolling}
        onTouchMove={markUserScrolling}
        onPointerDown={markUserScrolling}
        // Clear hover here, NOT on each card's mouseLeave. Otherwise crossing
        // the gap between two cards (which fires leave→enter) bounces the
        // viewer between the hovered page and the active page on every move.
        onPointerLeave={() => setHoveredField(null)}
      >
        {pageClusters.map((cluster) => {
          if (cluster.entries.length === 1) {
            return renderPageGroupEntry(cluster.entries[0]);
          }

          const first = cluster.entries[0];
          const pageLabel = first.page?.page_number
            ? t('pageLabel', { number: first.page.page_number })
            : `#${first.result.id}`;

          return (
            <div key={cluster.key}>
              <div className='flex items-center gap-2 px-5 pt-3 pb-2'>
                <Layers className='text-primary h-4 w-4 shrink-0' />
                <span className='text-sm font-medium'>{pageLabel}</span>
                <Badge variant='outline' className='text-[10px]'>
                  {t('templatePool')} · {cluster.entries.length}
                </Badge>
              </div>
              <div className='space-y-2 border-s ps-4'>
                {cluster.entries.map((entry) =>
                  renderPageGroupEntry(
                    entry,
                    entry.result.ocr_template?.name ??
                      templateNameById.get(
                        entry.result.ocr_template_id ?? -1
                      ) ??
                      undefined
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer: overall progress + submit all (hidden once submitted to QA, or while previewing frozen history) */}
      <AnimatePresence>
        {totalFieldCount > 0 &&
          !hasBeenSubmittedToQa &&
          capabilities.operatorToolsVisible && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className='space-y-2 border-t px-4 py-3 sm:px-5 sm:py-4'
            >
              <div className='space-y-1'>
                <div className='flex items-center justify-between text-xs'>
                  <span className='text-muted-foreground'>
                    {t('verificationProgress')}
                  </span>
                  <span className='font-medium'>
                    {verifiedFieldCount}/{totalFieldCount} ({overallProgress}
                    %)
                  </span>
                </div>
                <Progress value={overallProgress} className='h-1.5' />
              </div>

              <Button
                className='w-full'
                disabled={!isAllVerified || submitToQaMutation.isPending}
                onClick={handleSubmitAll}
              >
                {submitToQaMutation.isPending && (
                  <Loader2 className='me-2 h-4 w-4 animate-spin' />
                )}
                {t('submitAll')}
              </Button>
            </motion.div>
          )}
      </AnimatePresence>

      {/* The footer slot is a flex sibling of the page-sections scroll area,
          inside a column with a fixed height. Unwrapped, a tall footer wins
          that fight — `flex: 0 1 auto` with `min-height: auto` refuses to
          shrink below its content — and the scroll area collapses to
          `height: 0`, hiding every page section behind a viewport with no
          pixels. The supervisor's 30-row checklist did exactly that.

          `shrink-0` + a max-height + its own scroller bounds any footer,
          however long, so the sections keep the rest of the column. */}
      {!isPreviewMode && renderPanelFooter && (
        <div className='max-h-[45%] min-w-0 shrink-0 overflow-y-auto'>
          {renderPanelFooter()}
        </div>
      )}
    </>
  );

  return (
    <div className='space-y-3'>
      {/* Container header + QA status.
          ONE bar, not two. This screen had no header at all — no container
          name, no template name (OMA-626) — and the obvious fix, a header row
          above this one, is wrong: the viewer card below is sized
          `h-[calc(100svh-7rem)]` / `sm:h-[85svh]`, so another row pushes it
          past the viewport and the whole page starts scrolling, on the very
          screen we are trying to make roomier. Folding identity into the
          existing status strip costs no height and keeps ONE statement of the
          QA stage.

          The identity half renders on every surface — which container, read
          with which template, is useful to a reviewer too — while the operator
          tools stay behind `capabilities.operatorToolsVisible`, the
          discriminator that exists precisely so a QA screen cannot inherit
          them. */}
      <div
        className={cn(
          'flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border px-4 py-2.5 shadow-sm transition-colors',
          qaStatus.className
        )}
      >
        <div className='flex min-w-0 items-center gap-2.5'>
          <QaIcon
            className={cn('h-4 w-4 shrink-0', qaStatus.spin && 'animate-spin')}
          />
          <div className='flex min-w-0 flex-col leading-tight'>
            {containerSummary ? (
              <>
                <div className='flex min-w-0 items-center gap-1.5'>
                  <ContainerIdPrefix id={containerId} className='text-sm' />
                  {/* A container number — or an uploaded file name — carries
                      the document's own direction, never the shell's. */}
                  <span
                    dir='auto'
                    className='truncate text-sm font-semibold'
                    title={resolveOcrContainerLabel(
                      containerSummary,
                      t('untitledTestDocument')
                    )}
                  >
                    {resolveOcrContainerLabel(
                      containerSummary,
                      t('untitledTestDocument')
                    )}
                  </span>
                  {containerSummary.is_test && (
                    <Badge
                      variant='outline'
                      className='shrink-0 border-purple-500/40 text-[10px] text-purple-600 dark:text-purple-400'
                    >
                      {t('testBadge')}
                    </Badge>
                  )}
                </div>
                <span className='truncate text-[11px] opacity-75'>
                  {qaStatus.label}
                </span>
              </>
            ) : (
              /* Loading, or the summary read was refused. Falls back to
                 exactly what this bar said before, so a header that cannot
                 load is a missing addition rather than a broken bar. */
              <>
                <span className='text-[10px] font-medium tracking-wide uppercase opacity-75'>
                  {t('qaStatusLabel')}
                </span>
                <span className='text-sm font-semibold'>{qaStatus.label}</span>
              </>
            )}
          </div>
        </div>

        {/* OMA-626 — which template(s) this container is read with. Resolved
            server-side through the same precedence `startOcr` applies, so the
            header cannot name a template a run would not actually use. An
            empty pool is a normal state and says so rather than going blank. */}
        {containerSummary && (
          <div
            className='flex min-w-0 flex-wrap items-center gap-1.5'
            aria-label={t('template')}
          >
            {containerSummary.templates.length === 0 ? (
              <span className='text-xs opacity-70'>
                {t('noTemplateAssigned')}
              </span>
            ) : (
              <>
                {containerSummary.templates
                  .slice(0, HEADER_TEMPLATE_LIMIT)
                  .map((tpl) => (
                    <span
                      key={tpl.id}
                      className='flex min-w-0 items-center gap-1'
                    >
                      <Badge
                        variant='outline'
                        className='max-w-[14rem] truncate text-[11px] font-normal'
                      >
                        <span dir='auto' className='truncate'>
                          {tpl.name}
                        </span>
                      </Badge>
                      <TemplateFieldsInfo templateId={tpl.id} />
                    </span>
                  ))}
                {containerSummary.templates.length > HEADER_TEMPLATE_LIMIT && (
                  <Badge
                    variant='secondary'
                    className='shrink-0 text-[11px] font-normal'
                  >
                    +{containerSummary.templates.length - HEADER_TEMPLATE_LIMIT}
                  </Badge>
                )}
              </>
            )}
          </div>
        )}

        <div className='ms-auto flex shrink-0 items-center gap-1.5'>
          {hasBeenSubmittedToQa && (
            <span className='hidden text-xs opacity-80 lg:inline'>
              {t('qaSubmittedHint')}
            </span>
          )}

          {/* OMA-624 — show or hide the extraction panel, giving the document
              the full width. Not gated on `capabilities`: this is a layout
              control, not an operator tool, and every surface renders the same
              two-pane split. Desktop only — on touch the panel already lives
              in a Sheet with its own trigger, so there is nothing to collapse.
              The icons are deliberately NON-directional: the panel sits on the
              start edge under `ar` and the end edge under `en`, so a
              `PanelLeft`/`PanelRight` pair would point the wrong way in one of
              them. */}
          {!isMobile && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size='icon'
                  variant='ghost'
                  className='h-7 w-7'
                  onClick={togglePanel}
                  aria-label={
                    panelVisible ? t('hideResultsPanel') : t('showResultsPanel')
                  }
                  aria-pressed={!panelVisible}
                >
                  {panelVisible ? (
                    <Maximize2 className='h-3.5 w-3.5' />
                  ) : (
                    <Columns2 className='h-3.5 w-3.5' />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {panelVisible ? t('hideResultsPanel') : t('showResultsPanel')}
              </TooltipContent>
            </Tooltip>
          )}

          {/* OMA-627 and the header re-run.
              Gated on `operatorToolsVisible` ALONE. The in-panel button this
              replaces also required `qa_rejected`, so an operator whose
              container had never been submitted had no re-run on this screen
              at all — it existed only in the containers-listing sheet. */}
          {capabilities.operatorToolsVisible && (
            <PermissionGate permissions={['ocr.edit']}>
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* A disabled Button fires no pointer events, so the
                        tooltip needs a live wrapper to hang off — otherwise
                        the explanation is unreachable exactly when it is
                        needed. */}
                    <span>
                      <Button
                        size='sm'
                        variant='outline'
                        className='h-7 gap-1.5 text-xs'
                        disabled={rerunOcrMutation.isPending || isRunInFlight}
                        onClick={handleRerunOcr}
                      >
                        <RotateCcw
                          className={cn(
                            'h-3.5 w-3.5',
                            rerunOcrMutation.isPending && 'animate-spin'
                          )}
                        />
                        {rerunOcrMutation.isPending
                          ? t('retrying')
                          : t('rerunOcr')}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {isRunInFlight && (
                    <TooltipContent>{t('rerunBlockedInFlight')}</TooltipContent>
                  )}
                </Tooltip>

                {/* Re-running with a DIFFERENT template. The picker is the same
                    dialog the containers listing opens — it persists the pool
                    to the registration and then starts the run — so there is
                    exactly one rule for which templates a run uses. It needs a
                    registration: an ad-hoc test container has no pool to choose
                    from, which is why the control explains itself instead of
                    disappearing. */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        size='sm'
                        variant='outline'
                        className='h-7 gap-1.5 text-xs'
                        disabled={
                          isRunInFlight ||
                          rerunOcrMutation.isPending ||
                          containerSummary?.registration_id == null
                        }
                        onClick={() => setTemplatePickerOpen(true)}
                      >
                        <Layers className='h-3.5 w-3.5' />
                        {t('changeTemplateAndRerun')}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {(isRunInFlight ||
                    containerSummary?.registration_id == null) && (
                    <TooltipContent>
                      {isRunInFlight
                        ? t('rerunBlockedInFlight')
                        : t('rerunBlockedNoRegistration')}
                    </TooltipContent>
                  )}
                </Tooltip>

                {/* OMA-630 — add a field the extraction never produced.
                    Sits behind the same `operatorToolsVisible` gate as the
                    re-runs: a QA reviewer corrects values through their tier's
                    endpoint and must never author a new field, and an operator
                    whose container is in review has already handed it over.
                    `isPreviewMode` is folded into that gate already, so a
                    frozen history snapshot never offers it. */}
                <Button
                  size='sm'
                  variant='outline'
                  className='h-7 gap-1.5 text-xs'
                  disabled={isRunInFlight || pendingManualField != null}
                  onClick={() => {
                    // Draw flow: no page section owns this one.
                    setAddFieldTarget(null);
                    setAddFieldOpen(true);
                  }}
                >
                  <Plus className='h-3.5 w-3.5' />
                  {t('addField')}
                </Button>
              </>
            </PermissionGate>
          )}
        </div>
      </div>

      {/* History preview banner — replaces the live view with a frozen past
          suggestion run's snapshot; every accept/edit/submit control below
          hides for the duration (see isPreviewMode). */}
      {previewRun && (
        <div className='flex items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-amber-600 shadow-sm dark:text-amber-400'>
          <div className='flex items-center gap-2.5'>
            <History className='h-4 w-4' />
            <span className='text-sm font-semibold'>
              {t('viewingHistoryBanner', {
                date: previewRun.ran_at
                  ? new Date(previewRun.ran_at).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })
                  : ''
              })}
            </span>
          </div>
          <Button
            size='sm'
            variant='outline'
            className='h-7 text-xs'
            onClick={() => setPreviewRun(null)}
          >
            {t('backToCurrent')}
          </Button>
        </div>
      )}

      <div className='bg-background relative flex h-[calc(100svh-7rem)] min-h-[480px] overflow-hidden rounded-xl border shadow-sm sm:h-[85svh]'>
        {/* Left — Document pages viewer */}
        <div className='relative min-w-0 flex-1 md:border-e'>
          <DocumentPagesViewer
            pages={viewerPages}
            pdfUrl={viewerResults[0]?.pdf_url}
            highlightedChunkIds={highlightedChunkIds}
            activeChunkIds={activeChunkIds}
            activePage={effectiveViewerPage}
            onChunkClick={handleChunkClick}
            onChunkHover={handleChunkHover}
            onPageChange={handleViewerPageChange}
            onChunkBboxChange={
              enableBboxEditing && !isPreviewMode
                ? handleChunkBboxChange
                : undefined
            }
            pendingBboxes={pendingViewerBboxes}
            hiddenChunkIds={hiddenChunkIds}
            unsavedHighlights={unsavedViewerHighlights}
            // Two things arm the same drag: attaching a region to an existing
            // field, and placing the first region of one being created
            // (OMA-630). `handleArmManualFieldDraw` clears `drawTarget`, so
            // only ever one of them is live.
            drawHighlight={
              drawTarget || pendingManualField ? { active: true } : null
            }
            onDrawHighlight={
              pendingManualField
                ? handleDrawManualField
                : drawTarget
                  ? handleDrawHighlight
                  : undefined
            }
            onRemoveHighlight={
              enableBboxEditing && !isPreviewMode
                ? handleRemoveHighlight
                : undefined
            }
            // Recoloring is presentation-only (no `ocr.edit` needed, unlike
            // bbox editing), so QA reviewers get it too — but frozen history
            // snapshots stay look-only.
            onChunkColorChange={
              isPreviewMode ? undefined : handleChunkColorChange
            }
            overriddenColorChunkIds={overriddenColorChunkIds}
          />

          {/* Placing a new field's region (OMA-630). Its own bar rather than an
              arm of the one below: there is nothing to undo, discard or save
              here — the drag IS the save, because the field does not exist
              until the request that creates it succeeds. */}
          {pendingManualField && !isPreviewMode && (
            <div className='bg-background/95 absolute inset-x-3 top-3 z-30 flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 shadow-lg backdrop-blur'>
              <span className='text-xs font-medium'>
                {createOcrFieldMutation.isPending
                  ? t('addFieldSaving')
                  : t('addFieldDrawHint', { name: pendingManualField.name })}
              </span>
              <Button
                size='sm'
                variant='ghost'
                className='h-7 text-xs'
                onClick={() => setPendingManualField(null)}
                disabled={createOcrFieldMutation.isPending}
              >
                {t('cancel')}
              </Button>
            </div>
          )}

          {/* Highlight editing bar — OMA-592 / OMA-593. Appears the moment a
              box is moved, resized or drawn, and is the only way those reach
              the server. Also the in-app "you have unsaved changes" warning,
              since the App Router gives no navigation hook to intercept. */}
          {(drawTarget || hasUnsavedHighlights) && !isPreviewMode && (
            <div className='bg-background/95 absolute inset-x-3 top-3 z-30 flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 shadow-lg backdrop-blur'>
              <span className='text-xs font-medium'>
                {hasUnsavedHighlights
                  ? t('unsavedHighlightChanges', {
                      count: pendingHighlightCount
                    })
                  : t('drawHighlightHint')}
              </span>
              <div className='flex items-center gap-2'>
                <Button
                  size='icon'
                  variant='ghost'
                  className='h-7 w-7'
                  title={t('undo')}
                  aria-label={t('undo')}
                  onClick={undoHighlightDraft}
                  disabled={!canUndoHighlight || isSavingHighlights}
                >
                  <Undo2 className='h-3.5 w-3.5' />
                </Button>
                <Button
                  size='icon'
                  variant='ghost'
                  className='h-7 w-7'
                  title={t('redo')}
                  aria-label={t('redo')}
                  onClick={redoHighlightDraft}
                  disabled={!canRedoHighlight || isSavingHighlights}
                >
                  <Redo2 className='h-3.5 w-3.5' />
                </Button>
                <Button
                  size='sm'
                  variant='ghost'
                  className='h-7 text-xs'
                  onClick={discardHighlightEdits}
                  disabled={isSavingHighlights}
                >
                  {t('cancel')}
                </Button>
                <Button
                  size='sm'
                  className='h-7 text-xs'
                  onClick={saveHighlightEdits}
                  disabled={!hasUnsavedHighlights || isSavingHighlights}
                >
                  {isSavingHighlights && (
                    <Loader2 className='me-1.5 h-3 w-3 animate-spin' />
                  )}
                  {t('saveHighlight')}
                </Button>
              </div>
            </div>
          )}

          {/* Mobile FAB — opens extraction panel sheet */}
          {isMobile && (
            <Button
              size='sm'
              className='absolute end-3 bottom-16 z-20 shadow-lg md:hidden'
              onClick={() => setExtractionSheetOpen(true)}
              aria-label={t('extractedData')}
            >
              <ScanText className='h-4 w-4' />
              <span className='ms-2'>{t('extractedData')}</span>
              {totalFieldCount > 0 && (
                <Badge
                  variant='secondary'
                  className='ms-2 h-5 px-1.5 text-[10px] tabular-nums'
                >
                  {verifiedFieldCount}/{totalFieldCount}
                </Badge>
              )}
            </Button>
          )}
        </div>

        {/* Right — Extraction panel (inline on md+) */}
        {/* Both the handle and the panel drop out together. This row is a
            FLEX container, not the grid photo-scanning uses, so there is no
            placeholder to keep — omitting a flex child shifts nothing, and the
            viewer's `flex-1` simply takes the width back. */}
        {!isMobile && panelVisible && (
          <>
            <PanelResizeHandle {...extractionPanelResize.handleProps} />
            <div
              className='flex shrink-0 flex-col'
              style={{ width: extractionPanelResize.widthValue }}
            >
              {extractionPanel}
            </div>
          </>
        )}

        {/* Mobile/touch — Extraction panel inside a Sheet */}
        {isMobile && (
          <Sheet
            open={extractionSheetOpen}
            onOpenChange={setExtractionSheetOpen}
          >
            <SheetContent
              side='right'
              className='flex w-full max-w-full flex-col gap-0 p-0 sm:max-w-md'
            >
              <SheetTitle className='sr-only'>{t('extractedData')}</SheetTitle>
              {extractionPanel}
            </SheetContent>
          </Sheet>
        )}
      </div>

      {/* OMA-627 — pick a different template, then re-run against it. The
          dialog persists the chosen pool to the registration and starts the
          run itself, and `startOcr` archives the previous run rather than
          deleting it, so the old extraction stays readable in the history
          panel. Mounted only once a registration is known: without one the
          dialog has no pool to read, which is what the button's own tooltip
          explains. */}
      {capabilities.operatorToolsVisible &&
        containerSummary?.registration_id != null && (
          <OcrTemplatePoolDialog
            open={templatePickerOpen}
            onOpenChange={setTemplatePickerOpen}
            containerId={containerId}
            registrationId={containerSummary.registration_id}
            containerLabel={resolveOcrContainerLabel(
              containerSummary,
              t('untitledTestDocument')
            )}
            // A run already exists here, so "Assign per page" re-runs only
            // the ticked pages instead of restarting the container (OMA-674).
            rerunExistingRun={sortedResults.some((r) => r.status !== 'skipped')}
          />
        )}

      {/* Name / value / colour for a hand-added field (OMA-630). Opened from
          the toolbar the region is drawn on the document once this closes (see
          `handleDrawManualField`); opened from a page section there is no
          region and the field is saved on the spot. */}
      {capabilities.operatorToolsVisible && (
        <AddFieldDialog
          open={addFieldOpen}
          onOpenChange={setAddFieldOpen}
          mode={addFieldTarget ? 'save' : 'draw'}
          submitting={createOcrFieldMutation.isPending}
          defaultColor={defaultHighlightColor}
          onSubmit={(draft) => {
            if (addFieldTarget) {
              void handleSaveManualField(draft, addFieldTarget);
              return;
            }
            handleArmManualFieldDraw(draft);
          }}
        />
      )}
    </div>
  );
}
